import { Emitter } from "@core/events/emitter";
import { Ioc } from "@core/ioc/ioc";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import { PERMISSIONS_MODEL } from "@models/permissions/IPermissionsModel";
import { accountRoleToSharedUserRole } from "@models/data/userRolePermissions";
import { SHARED_USER_ROLE } from "@models/data/sharedLinkDto";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { UserInfo } from "@models/auth/types";
import type { IEventModel, ServerEvent } from "@models/events/IEventModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import { loadCampfireEnv } from "@models/storage/cloudConfig";
import type {
  FollowChange,
  IMultiuserSessionModel,
  MultiuserParticipant,
  MultiuserParticipantKind,
  ParticipantQuat,
  ParticipantTransform,
  ParticipantVec3,
  PresenterChange,
  SessionParticipantsQuery,
} from "./IMultiuserSessionModel";
import {
  isParticipantInSession,
  isQuestControllerKey,
  participantPoseIsTracked,
  PRESENTER_TOOLS,
} from "./IMultiuserSessionModel";
import { getAppInstanceId, isOwnAppInstance } from "./appInstanceId";

/**
 * Roster upsert payload: `isPresenter` / `canBePresenter` / `currentSceneId`
 * are tri-state — `null`/`undefined` means "the payload didn't carry the
 * field, keep the existing value" (heartbeats and transform events don't
 * repeat them).
 */
type UpsertParticipant = Omit<
  MultiuserParticipant,
  | "isPresenter"
  | "canBePresenter"
  | "currentSceneId"
  | "activeTool"
  | "projectId"
  | "projectCustomerId"
  | "userRole"
  | "appName"
  | "kind"
  | "hasHeadset"
  | "transform"
> & {
  /**
   * Tri-state, like the presence fields below, and for the same reason: only
   * a ROSTER payload (`launch_application` / `open_project` / a shared-state
   * row) carries the app name, so `participant_transform` — which is a bare
   * `ParticipantTransformEventData` — must keep the known one instead of
   * resetting the peer to `unknown` and flipping their avatar to the fallback
   * screen frame. `kind` is derived from it and travels with it.
   */
  readonly appName?: string | null;
  readonly kind?: MultiuserParticipantKind | null;
  readonly hasHeadset?: boolean | null;
  /**
   * A pose, or absent. There is deliberately no "clear the pose" spelling:
   * Unity's `SessionParticipantInfo.Transform` lives on a long-lived record
   * that only ever has REAL poses written to it, so a payload without one
   * leaves the peer's last known pose standing.
   */
  readonly transform?: ParticipantTransform | null;
  readonly isPresenter?: boolean | null;
  /** Tri-state like its neighbours: absent keeps the peer's known role. */
  readonly userRole?: number | null;
  readonly canBePresenter?: boolean | null;
  readonly currentSceneId?: string | null;
  /** Tri-state like the others: absent keeps the known value (heartbeats
   *  repeat the current tool; `active_tool` events are authoritative). */
  readonly activeTool?: number | null;
  /**
   * Tri-state, and the distinction MATTERS more here than anywhere else:
   * `undefined` means the payload carried no project id (Unity's heartbeat is
   * a bare `BaseEventData` — it has no `project_id` field at all), so the
   * known value must be kept; `null` means the participant is explicitly in
   * NO project (`close_project`). Collapsing the two is what let a peer's
   * heartbeat wipe their known project and land them in everyone's roster.
   */
  readonly projectId?: string | null;
  readonly projectCustomerId?: string | null;
};

const HEARTBEAT_MS = 5_000;
// Throttle for the "heartbeat from a participant we don't have" resync — see
// `refreshHeartbeat`. Long enough that a busy account can't turn idle peers'
// heartbeats into a snapshot-request storm.
const UNKNOWN_HEARTBEAT_RESYNC_MS = 10_000;
// Unity `MultiuserSessionModel.CoHousekeeping` cadence — offline sweep +
// presenter self-election run at 1 Hz.
const HOUSEKEEPING_MS = 1_000;
// Unity `MultiuserAppModel.k_minSecondsBetweenPresenterChanges`: receivers
// ignore `presenter_change` events arriving faster than this, which is the
// entire conflict-resolution mechanism for simultaneous self-elections.
const K_MIN_MS_BETWEEN_PRESENTER_CHANGES = 3_000;
// How long presenter self-election stays suppressed while a requested
// `shared_state_snapshot` response is outstanding. Unity suspends its
// housekeeping coroutine for the whole window; the web adds a deadline so a
// lost response cannot disable self-election permanently.
const SHARED_STATE_WAIT_MS = 10_000;
// Unity `ParticipantTransformData` change-detection tolerances
// (`SharedStateEventData.cs`, relaxed in the 2026-07 Eva merge): re-send only
// when position moved > 1 cm (squared-magnitude compare) or rotation > 5°.
const K_MIN_REQUIRED_POS_MAGNITUDE_SQ = 0.01 * 0.01;
const K_MIN_REQUIRED_ROT_ANGLE_DEG = 5;
// Unity `ThrottleUtils.k_defaultFrequency` (50 Hz): the sender-side cap on
// `participant_transform` publishes (`AWSServerEventSenderModel
// .SendChangeParticipantTransformEvent`). A too-soon publish is DROPPED, not
// deferred — callers sample every frame, so the next frame retries; a
// tool-visibility flip bypasses the cap entirely (Unity `byPassThrottle`).
const K_TRANSFORM_PUBLISH_MIN_INTERVAL_MS = 1000 / 50;
// Unity `QuestLocalUserModel.k_notIntializedPos`: the pose published while the
// user has no real pose yet — far offscreen, never the (0,0,0) sentinel.
const K_NOT_INITIALIZED_POS: ParticipantVec3 = [10000, 10000, 10000];
// Unity `MultiuserAppModel.k_maxSessionIdleSeconds` (= 3 x the 5 s heartbeat
// interval). Used BOTH ways, exactly as upstream: the local 1 Hz sweep evicts a
// peer we have not heard from in this long, and `sharedStateRowIsLive` applies
// the same ceiling to the SERVER's own last-heartbeat stamp on snapshot rows.
const OFFLINE_AFTER_MS = 15_000;
const DESKTOP_PRODUCT_ID_DEV = "com.campfire3d.scenesdesktop-dev";
const DESKTOP_PRODUCT_ID_PROD = "com.campfire3d.scenesdesktop";
const REMOTE_PRODUCT_ID_DEV = "com.campfire3d.scenesremote-dev";
const REMOTE_PRODUCT_ID_PROD = "com.campfire3d.campfirexr-remote";
const TABLET_PRODUCT_ID_DEV = "com.campfire3d.scenes-dev";
const TABLET_PRODUCT_ID_PROD = "com.campfire3d.campfirexr";
const ANDROID_PRODUCT_IDS = new Set([
  "com.campfire3d.scenesdroiddev",
  "com.campfire3d.campfirequest",
  "com.campfire3d.campfirequestq4b",
  "com.campfire3d.androidxrdev",
  "com.campfire3d.androidxr",
]);
const QUESTLINK_PRODUCT_IDS = new Set([
  "com.campfire3d.scenesquestlink-dev",
  "com.campfire3d.scenesquestlink",
]);
const WEB_DEVICE_ID = "campfire-scenes-web";
const PARTICIPANT_COLORS: ReadonlyArray<string> = [
  "#ff0000",
  "#0078ff",
  "#00ff00",
  "#ff8000",
  "#8000ff",
  "#ff0080",
  "#80ff00",
  "#8080ff",
  "#14c38e",
  "#e3fcbf",
  "#feb139",
  "#d2ff0a",
  "#f479a2",
  "#64148c",
  "#746bab",
  "#d9c7db",
  "#58445c",
  "#ff8c00",
  "#ffff00",
  "#7f0000",
  "#0000ff",
];
const DEBUG_MULTIUSER = import.meta.env.DEV
  || import.meta.env.VITE_CAMPFIRE_MULTIUSER_DEBUG === "true"
  || window.localStorage.getItem("campfire.multiuser.debug") === "true";

/**
 * Basic web presence/session model.
 *
 * Mirrors the roster responsibility of Unity's `MultiuserAppModel` while the
 * heavier WebRTC/session handoff remains future M12 work.
 */
export class MultiuserSessionModel implements IMultiuserSessionModel {
  readonly onParticipantsChanged = new Emitter<ReadonlyArray<MultiuserParticipant>>();
  readonly onParticipantTransformChanged = new Emitter<MultiuserParticipant>();
  readonly onParticipantJoined = new Emitter<MultiuserParticipant>();
  readonly onPresenterChanged = new Emitter<PresenterChange>();
  readonly onFollowChanged = new Emitter<FollowChange>();
  readonly onLocalFollowTargetChanged = new Emitter<MultiuserParticipant | null>();
  readonly onAskedToFollow = new Emitter<MultiuserParticipant>();

  private readonly m_participants = new Map<string, MultiuserParticipant>();
  private readonly m_appInstanceId = getAppInstanceId();
  private readonly m_appName = loadCampfireEnv() === "prod"
    ? DESKTOP_PRODUCT_ID_PROD
    : DESKTOP_PRODUCT_ID_DEV;
  private readonly m_colorIndex = colorIndexFromString(this.m_appInstanceId);
  private m_localParticipantKey: string | null = null;
  /** Last project id seen — a same-id re-emit (e.g. rename) must not reset the roster. */
  private m_lastProjectId: string | null = null;
  private m_openProjectIdPendingClose: string | null = null;
  private m_openProjectStartedAt = 0;
  private m_loadedSceneIdPendingUnload: string | null = null;
  private m_loadedSceneStartedAt = 0;
  private m_timer: number | null = null;
  private m_housekeepingTimer: number | null = null;
  private m_offFns: Array<() => void> = [];
  private m_started = false;
  /** Last transform actually published — Unity `m_currentTransformData` dedup cache. */
  private m_lastPublishedTransform: ParticipantTransform | null = null;
  private m_lastTransformPublishAtMs = 0;
  /**
   * The outbound merge base — Unity `DesktopLocalUserModel.m_transformData`.
   * Local writers only; deliberately NOT the roster record, which inbound
   * paths can overwrite with stale echoes (see `updateLocalTransform`).
   */
  private m_localComposedTransform: ParticipantTransform | null = null;
  /** Local `PresenterTools` flags — stamped as `at` on presence payloads. */
  private m_localActiveTool = 0;
  /** True while the local user presents an immersive WebXR session. */
  private m_localHasHeadset = false;
  /**
   * AI-1150 initial-scene self-heal: one-shot flag set on project open —
   * until the FIRST scene change after open (user pick, presenter follow, or
   * this retry), keep trying to join the remote presenter's scene as its
   * `sid` becomes known (shared state / load_scene / housekeeping). The web
   * always activates SOME scene on load (no black-viewport failure mode),
   * so this is purely the join-time "prefer the presenter's scene" parity.
   */
  private m_needsInitialSceneNavigation = false;
  /** Swallows the load's own initial scene emit (see the subscription). */
  private m_initialSceneEmitPending = false;
  /**
   * Shared follow graph, follower key → followed key. Unity
   * `MultiuserAppModel.m_followerKeyToFollowedUserKey` — fed by
   * `follower_change` echoes and shared-state `fu` fields, never written
   * optimistically (the network state is the single source of truth).
   */
  private readonly m_followerToFollowed = new Map<string, string>();
  /**
   * The LOCAL follow target (Unity `ILocalUserModel.FollowTarget`). Unlike the
   * graph this IS set immediately on `followUser` so the camera + overlay
   * react without waiting for the echo, matching Unity's split.
   */
  private m_localFollowedKey: string | null = null;
  /** Unity `m_lastChangePresenterProcessed` — receiver-side rate limit. */
  private m_lastPresenterChangeProcessedAt = 0;
  /**
   * Non-zero while a `shared_state_snapshot` response is pending (deadline
   * timestamp). Unity STOPS the housekeeping coroutine between requesting
   * shared state and receiving it (`RequestSharedStateAsync` →
   * `StopMultiuserSession`; restarted in `OnAppModelSharedStateReceived`) so
   * `EnsurePresenter` can never self-elect against a roster it hasn't seen
   * yet — without this, a joiner racing the snapshot RTT steals
   * presentership from the legitimate presenter session-wide. The deadline
   * (vs Unity's hard stop) keeps a lost response from disabling
   * self-election forever — the web relay has no ack guarantee.
   */
  private m_awaitingSharedStateUntil = 0;
  /**
   * The last `followedUserKey` this client PUBLISHED ("" = stop). Own
   * unfollow echoes are applied to local follow state only when "" is still
   * the latest published intent — a stop echo arriving after a newer
   * `followUser(B)` publish is stale and must not cancel the B follow
   * (the graph still converges to B via the later echo). Deviation from
   * Unity, whose stale echo cancels the new follow and re-publishes "" —
   * both end consistent, but the web keeps the user's newer intent.
   */
  private m_lastPublishedFollowKey: string | null = null;
  /**
   * Rate limit for the unknown-heartbeat resync below. Unity has the same
   * `RequestSharedState()` call at that site but leaves it COMMENTED OUT
   * (`MultiuserAppModel.UpdateLastHeartbeat`), because on desktop the roster
   * is never emptied and the case is rare. The web clears its roster on every
   * project switch, so the gap is genuinely reachable here and the request is
   * worth making — throttled, since an account with many idle clients would
   * otherwise re-request on every heartbeat that isn't ours.
   */
  private m_lastUnknownHeartbeatResyncAt = 0;

  constructor(
    private readonly m_auth: IAuthModel,
    private readonly m_events: IEventModel,
    private readonly m_project: IProjectModel,
  ) {}

  get participants(): ReadonlyArray<MultiuserParticipant> {
    // Unity parity: offline participants stay in `m_participants` (so a
    // reconnect keeps its reserved color/display name, and internal reads can
    // still see leave history), but every roster surface excludes them — see
    // `MultiuserAppModel.GetSessionParticipants(excludeOffline: true)` and
    // `ParticipantsListViewModel` (`.Where(p => !p.IsOffline)`). The local
    // participant is always `isOnline: true`, so this never hides "You".
    // Quest-controller rows (`"{aid}-qc"` keys) are transform carriers, not
    // people — Unity routes them straight to the hand/controller visuals and
    // never lists them; until the controller-mesh layer lands the web must
    // exclude them here or a Quest peer produces phantom avatars + roster rows.
    return [...this.m_participants.values()]
      .filter((participant) => participant.isOnline && !isQuestControllerKey(participant.key))
      .sort((a, b) => Number(b.isLocal) - Number(a.isLocal) || a.displayName.localeCompare(b.displayName));
  }

  /**
   * Port of `MultiuserAppModel.GetSessionParticipants` (`:580`) — the roster
   * scoped to one project via `IsInSession`, which is how Unity keeps an
   * account-wide roster from leaking into a project's surfaces.
   *
   * Unity's C# defaults are `excludeRemote: true` (Campfire Remote is a phone
   * companion, not a seat) and `excludeOffline: false`, but every caller that
   * renders people passes `.Where(p => !p.IsOffline)` immediately after, so
   * offline defaults TRUE here instead of making every call site repeat it.
   */
  getSessionParticipants(query: SessionParticipantsQuery): ReadonlyArray<MultiuserParticipant> {
    const excludeRemote = query.excludeRemote ?? true;
    const excludeOffline = query.excludeOffline ?? true;
    return [...this.m_participants.values()]
      .filter((participant) => {
        // Quest-controller rows are transform carriers, not people — Unity
        // routes them straight to the hand/controller visuals.
        if (isQuestControllerKey(participant.key)) return false;
        if (excludeOffline && !participant.isOnline) return false;
        if (excludeRemote && participant.kind === "remote") return false;
        return isParticipantInSession(participant, query.projectId, query.accountId);
      })
      .sort((a, b) => Number(b.isLocal) - Number(a.isLocal) || a.displayName.localeCompare(b.displayName));
  }

  /**
   * The account that OWNS the open project — Unity's
   * `m_customerModel.CurrentProject.CustomerId`, which is what both sides of
   * `IsInSession`'s account term are built from. Only a cross-account share
   * carries `ProjectInfo.customerId`; on your own projects it falls back to
   * your account, the same resolution `ProjectModel.getStorageContext` uses.
   */
  private activeProjectAccountId(): string | null {
    return this.m_project.activeProject?.customerId
      ?? this.m_auth.currentUser?.customerId
      ?? null;
  }

  get localParticipantKey(): string | null {
    return this.m_localParticipantKey;
  }

  get localFollowedKey(): string | null {
    return this.m_localFollowedKey;
  }

  /**
   * Port of `MultiuserAppModel.GetPresenter(projectId, accountId)` (`:713`) —
   * scoped to the OPEN project. Reading the global roster here would let
   * another project's presenter answer, and (via `ensurePresenter` below)
   * suppress this project's own self-election indefinitely.
   */
  getPresenter(): MultiuserParticipant | null {
    for (const participant of this.getActiveProjectParticipants({ excludeRemote: false })) {
      if (participant.isPresenter) return participant;
    }
    return null;
  }

  /** The open project's participants — the query every in-project read wants. */
  getActiveProjectParticipants(
    options: Pick<SessionParticipantsQuery, "excludeRemote" | "excludeOffline"> = {},
  ): ReadonlyArray<MultiuserParticipant> {
    return this.getSessionParticipants({
      projectId: this.m_project.activeProjectId,
      accountId: this.activeProjectAccountId(),
      ...options,
    });
  }

  getFollowedKeyOf(key: string): string | null {
    return this.m_followerToFollowed.get(key) ?? null;
  }

  isUserFollowing(key: string): boolean {
    const followed = this.m_followerToFollowed.get(key);
    return followed !== undefined && followed.length > 0;
  }

  /**
   * Unity `MultiuserAppModel.CanFollow` + `CircularFollow`: simulate the
   * local user following `key` and reject if that closes a follow cycle.
   */
  canFollow(key: string): boolean {
    const self = this.m_localParticipantKey;
    if (!self) return false;
    const graph = new Map(this.m_followerToFollowed);
    graph.set(self, key);
    let cursor: string | undefined = graph.get(self);
    while (cursor) {
      if (cursor === self) return false;
      cursor = graph.get(cursor);
    }
    return true;
  }

  /**
   * Port of `NetworkUsersModel.GetIsUserPosLive` (`:522`) — which is NOT
   * "have we ever received a pose from this peer". Unity asks the AVATAR
   * OBJECT it spawned for them, and reads `IsPosLive` off it:
   *
   * - a screen peer (`DisplayBehaviour` / `TabletBehaviour`) never starts
   *   `CoUpdatePosLiveState`, so `IsPosLive` keeps its `true` default forever
   *   — a desktop peer is followable the moment they are in the room, pose or
   *   no pose. Unity even spawns them at `k_initialNetworkerPos`
   *   (10000,10000,10000) and still says live;
   * - a tracked device (`HeadsetBehaviour` / `QuestBehaviour` /
   *   `RemoteBehaviour`) runs the coroutine, which is the only thing that
   *   clears the flag, and Quest holds it false until a live transform lands
   *   (`QuestBehaviour.ResetLiveVisibilityState`);
   * - no avatar at all — a peer we don't have, or who left the session — is
   *   not live: `GetIsUserPosLive` returns false for an unknown key.
   *
   * The web previously demanded a stored pose from EVERYONE, which is why
   * "Follow" refused a desktop peer who was plainly standing there: senders
   * change-gate at 1 cm / 5°, so a peer sitting still transmits nothing, and
   * any gap in the stored pose lasted until they moved again.
   */
  isParticipantTracking(key: string): boolean {
    const participant = this.m_participants.get(key);
    if (!participant || !participant.isOnline) return false;
    if (!participantPoseIsTracked(participant)) return true;
    const position = participant.transform?.position;
    if (!position) return false;
    return !(position[0] === K_NOT_INITIALIZED_POS[0]
      && position[1] === K_NOT_INITIALIZED_POS[1]
      && position[2] === K_NOT_INITIALIZED_POS[2]);
  }

  /**
   * Unity `MultiuserAppModel.MakePresenterAsync`: broadcast only — the flag
   * flips when our own `presenter_change` echo returns (network state is the
   * single source of truth, `AWSServerEventListenerModel.cs:200-227`).
   */
  makePresenter(key: string): void {
    if (!this.m_participants.has(key)) {
      debug("makePresenter skipped: unknown participant", key);
      return;
    }
    debug("makePresenter", key);
    this.publish("presenter_change", { presenterId: key });
  }

  /**
   * Port of `ParticipantsListItemViewModel.OnFollow` +
   * `ILocalUserModel.FollowTarget`: guard, broadcast `follower_change`, and
   * enter local follow mode immediately (camera + overlay bind to
   * `onLocalFollowTargetChanged`; callers surface the "not tracking"
   * confirmation when this returns false).
   */
  followUser(key: string): boolean {
    const target = this.m_participants.get(key);
    const self = this.m_localParticipantKey;
    if (!target || !self || key === self || !target.isOnline) return false;
    if (!this.isParticipantTracking(key)) return false;
    if (!this.canFollow(key)) return false;
    debug("followUser", key);
    this.m_lastPublishedFollowKey = key;
    this.publish("follower_change", { followedUserKey: key });
    if (this.m_localFollowedKey !== key) {
      this.m_localFollowedKey = key;
      this.onLocalFollowTargetChanged.emit(target);
    }
    return true;
  }

  /** Port of `AudienceFollowViewModel.Cancel` → `FollowAsync(string.Empty)`. */
  stopFollowing(): void {
    this.endLocalFollow(true);
  }

  private endLocalFollow(publishChange: boolean): void {
    if (this.m_localFollowedKey === null) return;
    debug("endLocalFollow", { publishChange, was: this.m_localFollowedKey });
    this.m_localFollowedKey = null;
    if (publishChange) {
      this.m_lastPublishedFollowKey = "";
      this.publish("follower_change", { followedUserKey: "" });
    }
    this.onLocalFollowTargetChanged.emit(null);
  }

  start(): void {
    if (this.m_started) return;
    debug("start", {
      eventState: this.m_events.state,
      activeProjectId: this.m_project.activeProjectId,
      hasUser: Boolean(this.m_auth.currentUser),
    });
    this.m_started = true;
    this.m_offFns = [
      this.m_auth.onSignedIn.on((user) => this.setLocalUser(user)),
      this.m_auth.onSignedOut.on(() => this.clear()),
      this.m_project.onActiveProjectChanged.on(() => this.handleActiveProjectChanged()),
      this.m_project.onBeginUnloadProject.on(({ projectId }) =>
        this.handleBeginUnloadProject(projectId)),
      // AI-1150 self-heal flag lifecycle: the FIRST scene emit after a
      // project switch is the load's own default activation (project-changed
      // fires first, then the initial scene emit — `ProjectModel.ts:383-385`)
      // and must not clear the flag; any LATER scene change (user pick,
      // presenter follow, or the retry itself) resolves the intent (Unity
      // `OnBeginAsyncNavigation` clears on any scene navigation).
      this.m_project.onActiveSceneChanged.on((scene) => {
        if (this.m_initialSceneEmitPending) {
          this.m_initialSceneEmitPending = false;
        } else {
          this.m_needsInitialSceneNavigation = false;
        }
        this.handleActiveSceneChanged(scene?.id ?? null);
      }),
      this.m_events.onEvent.on((event) => this.handleEvent(event)),
      this.m_events.onStateChanged.on((state) => {
        if (state === "connected") {
          this.handleConnected();
        } else {
          // Unity `OnServerChangeSessionState` (`MultiuserAppModel.cs:977-983`):
          // a dropped websocket locally unfollows the self (no publish — the
          // socket is gone). The graph re-syncs from shared state on reconnect.
          this.endLocalFollow(false);
        }
      }),
    ];
    const permissions = this.permissions();
    if (permissions) {
      this.m_offFns.push(
        permissions.onPermissionsChanged.on(() => this.syncLocalPermissions()),
      );
    }
    if (this.m_auth.currentUser) this.setLocalUser(this.m_auth.currentUser);
    this.m_timer = window.setInterval(() => this.tick(), HEARTBEAT_MS);
    this.m_housekeepingTimer = window.setInterval(() => this.housekeeping(), HOUSEKEEPING_MS);
    this.publishHeartbeat();
    if (import.meta.env.DEV) this.installDevFakeParticipants();
  }

  /**
   * Dev-only manual QA hook: `window.__campfireInjectFakeUsers()` seeds one
   * remote participant per representation (desktop screen, desktop+HMD head,
   * tablet, quest, remote phone) around the table so avatar rendering can be
   * verified without a second client. `window.__campfireClearFakeUsers()`
   * removes them.
   */
  private installDevFakeParticipants(): void {
    const win = window as unknown as Record<string, unknown>;
    let keepAlive: number | null = null;
    let staged: MultiuserParticipant[] = [];
    const reupsert = () => {
      // Tri-state null so a keepalive refresh never stomps presenter state
      // injected via `__campfireSimulateEvent("presenter_change", ...)`.
      for (const participant of staged) this.upsert({ ...participant, isPresenter: null });
    };
    // QA hook: feed a synthetic server event through the normal inbound
    // pipeline (the socket-injection equivalent used to verify inbound
    // handling when the dev relay doesn't carry an event family web↔web).
    win.__campfireSimulateEvent = (type: string, payload: Record<string, unknown>) => {
      this.handleEvent({ type, payload });
    };
    win.__campfireInjectFakeUsers = (withTool = false, distance = 2.8) => {
      const specs: ReadonlyArray<{
        name: string;
        appName: string;
        hasHeadset: boolean;
        colorIndex: number;
      }> = [
        { name: "Dana D", appName: DESKTOP_PRODUCT_ID_PROD, hasHeadset: false, colorIndex: 0 },
        { name: "Harper H", appName: DESKTOP_PRODUCT_ID_PROD, hasHeadset: true, colorIndex: 1 },
        { name: "Tya T", appName: TABLET_PRODUCT_ID_PROD, hasHeadset: false, colorIndex: 2 },
        { name: "Quinn Q", appName: "com.campfire3d.campfirequest", hasHeadset: true, colorIndex: 3 },
        { name: "Rey R", appName: REMOTE_PRODUCT_ID_PROD, hasHeadset: false, colorIndex: 4 },
      ];
      // Spawn on an arc in front of the local camera (Unity space) so the
      // fakes land in view wherever the orbit camera currently sits. The
      // arc is computed once per inject call; the keepalive only refreshes
      // liveness so the avatars can be orbited around afterwards.
      const local = this.localParticipant()?.transform ?? null;
      const eye: [number, number, number] = local ? [...local.position] : [0, 1.4, -3];
      const q = local?.rotation ?? [0, 0, 0, 1];
      const forward: [number, number, number] = [
        2 * (q[0] * q[2] + q[3] * q[1]),
        2 * (q[1] * q[2] - q[3] * q[0]),
        1 - 2 * (q[0] * q[0] + q[1] * q[1]),
      ];
      const flat = Math.hypot(forward[0], forward[2]) || 1;
      const dir: [number, number] = [forward[0] / flat, forward[2] / flat];
      // Center the arc on the view ray (full 3D forward) so the fakes land
      // mid-screen regardless of camera pitch.
      const center: [number, number, number] = [
        eye[0] + forward[0] * distance,
        eye[1] + forward[1] * distance,
        eye[2] + forward[2] * distance,
      ];
      const right: [number, number] = [dir[1], -dir[0]];
      staged = specs.map((spec, i) => {
        const offset = (i - (specs.length - 1) / 2) * 0.85;
        const px = center[0] + right[0] * offset;
        const pz = center[2] + right[1] * offset;
        // Face back toward the local camera (Unity yaw = atan2(dx, dz)).
        const yaw = Math.atan2(eye[0] - px, eye[2] - pz);
        return {
          key: `fake-user-${i}:fake-app-${i}`,
          userId: `fake-user-${i}`,
          appInstanceId: `fake-app-${i}`,
          appName: spec.appName,
          displayName: spec.name,
          initials: spec.name.split(" ").map((p) => p[0]).join("").toUpperCase(),
          email: null,
          projectId: this.m_project.activeProjectId,
          projectCustomerId: this.activeProjectAccountId(),
          color: PARTICIPANT_COLORS[spec.colorIndex],
          isLocal: false,
          isOnline: true,
          kind: participantKindFor(spec.appName),
          hasHeadset: spec.hasHeadset,
          lastSeenAt: Date.now(),
          transform: {
            position: [px, center[1], pz],
            rotation: [0, Math.sin(yaw / 2), 0, Math.cos(yaw / 2)],
            toolPosition: withTool && i === 0 ? [center[0], 0.4, center[2]] : null,
            toolVisibility: withTool && i === 0 ? 1 : 0,
          },
          isPresenter: false,
          canBePresenter: true,
          currentSceneId: this.m_project.activeSceneId,
          // Fake laser-wielder advertises the laser tool so the renderer's
          // active-tool gate lets its beam through.
          activeTool: withTool && i === 0 ? PRESENTER_TOOLS.LaserTool : PRESENTER_TOOLS.None,
        };
      });
      if (keepAlive === null) {
        // Re-upsert under the offline timeout so fakes persist until cleared.
        keepAlive = window.setInterval(reupsert, OFFLINE_AFTER_MS / 2);
      }
      reupsert();
    };
    win.__campfireClearFakeUsers = () => {
      if (keepAlive !== null) {
        window.clearInterval(keepAlive);
        keepAlive = null;
      }
      staged = [];
      let removed = false;
      for (const key of [...this.m_participants.keys()]) {
        if (key.startsWith("fake-user-")) {
          this.m_participants.delete(key);
          removed = true;
        }
      }
      if (removed) this.onParticipantsChanged.emit(this.participants);
    };
  }

  stop(): void {
    if (!this.m_started) return;
    debug("stop");
    this.sendPendingProjectClose();
    this.publish("quit_application", {
      appName: this.m_appName,
      app_name: this.m_appName,
      AppName: this.m_appName,
      device_id: WEB_DEVICE_ID,
      DeviceId: WEB_DEVICE_ID,
      DeviceType: "Web",
    });
    for (const off of this.m_offFns) off();
    this.m_offFns = [];
    if (this.m_timer !== null) window.clearInterval(this.m_timer);
    this.m_timer = null;
    if (this.m_housekeepingTimer !== null) window.clearInterval(this.m_housekeepingTimer);
    this.m_housekeepingTimer = null;
    this.m_started = false;
    this.clear();
  }

  updateLocalTransform(transform: ParticipantTransform): void {
    const key = this.m_localParticipantKey;
    if (!key) return;
    const current = this.m_participants.get(key);
    if (!current) return;
    // Unity composes ONE `ParticipantTransformData` per frame — camera pose
    // and tool fields together (`DesktopLocalUserModel.LateUpdate`). The web
    // has two writers (the per-frame camera/HMD sampler sends pose only; the
    // presenter-tools publisher adds `tp`/`tv`), so absent fields inherit the
    // last LOCALLY COMPOSED transform. Never merge from the roster record:
    // inbound writers (shared-state rows, a relay's own echo) can overwrite
    // it with RTT-stale data, and a stale `tv` read back into the outbound
    // path would re-publish state the user already left — a feedback loop
    // Unity structurally cannot have (its `m_transformData` is composed from
    // local inputs only; the roster is a separate struct).
    const base = this.m_localComposedTransform;
    // Unity quantizes on struct assignment (positions to mm, quaternion
    // components to 2 decimals) so wire values match across clients.
    const quantized = quantizeTransform(base ? { ...base, ...transform } : transform);
    this.m_localComposedTransform = quantized;
    // AI-1188: an exactly-(0,0,0) position is the "uninitialized" sentinel —
    // never stored, emitted, or published (heartbeats substitute
    // `k_notIntializedPos` for a missing pose anyway). Returning BEFORE the
    // change gate also keeps a persistent zero pose from failing the gate
    // every frame into unbounded roster churn.
    if (isZeroPosition(quantized.position)) return;
    // Unity's `LateUpdate` change gate (`ParticipantTransformData.Equals`,
    // 1 cm / 5° / exact tool + controller fields): callers sample every
    // frame, so an insignificant change does nothing at all — no roster
    // churn, no emit, no publish.
    const last = this.m_lastPublishedTransform;
    if (last !== null && transformsApproximatelyEqual(last, quantized)) return;
    // Unity `ThrottleUtils` (50 Hz, drop-not-defer): a too-soon sample does
    // NOTHING — the next frame retries, so the trailing pose always lands —
    // and the roster write + emit below ride the same cadence as the wire
    // (emitting before this gate would spam listeners at display rate during
    // motion). performance.now() because the wall clock can step backwards;
    // the remainder carry mirrors Unity's `m_secSinceLastUpdate %= window`,
    // without which a 60 Hz frame loop aliases the 20 ms window down to
    // ~30 Hz instead of ~50. A laser show/hide bypasses the cap (Unity
    // `byPassThrottle`) and, like Unity's direct send, skips the accumulator.
    const toolVisibilityChanged =
      last !== null && (last.toolVisibility ?? 0) !== (quantized.toolVisibility ?? 0);
    if (!toolVisibilityChanged) {
      const now = performance.now();
      const elapsed = now - this.m_lastTransformPublishAtMs;
      if (elapsed < K_TRANSFORM_PUBLISH_MIN_INTERVAL_MS) return;
      this.m_lastTransformPublishAtMs = now - (elapsed % K_TRANSFORM_PUBLISH_MIN_INTERVAL_MS);
    }
    const next = { ...current, transform: quantized, lastSeenAt: Date.now() };
    this.m_participants.set(key, next);
    this.onParticipantTransformChanged.emit(next);
    this.m_lastPublishedTransform = quantized;
    this.publish("participant_transform", {
      Transform: quantized,
      transform: quantized,
      t: transformToWire(quantized),
    });
  }

  setLocalHasHeadset(hasHeadset: boolean): void {
    if (this.m_localHasHeadset === hasHeadset) return;
    this.m_localHasHeadset = hasHeadset;
    // One immediate presence event so peers re-type the avatar now rather
    // than on the next 5 s heartbeat (setLocalActiveTool pattern).
    if (this.m_project.activeProjectId) this.publishHeartbeat();
  }

  setLocalActiveTool(activeTool: number): void {
    if (this.m_localActiveTool === activeTool) return;
    this.m_localActiveTool = activeTool;
    const local = this.localParticipant();
    if (local) {
      this.m_participants.set(local.key, { ...local, activeTool, lastSeenAt: Date.now() });
      this.onParticipantsChanged.emit(this.participants);
    }
    if (!this.m_project.activeProjectId) return;
    // Unity `SendChangeActiveToolEvent`: one event per CHANGE, stamping the
    // current tool point/visibility; continuous updates ride `tp`/`tv`.
    const transform = local?.transform ?? null;
    const toolPosition = transform?.toolPosition ?? null;
    debug("publish active_tool", { activeTool });
    this.publish("active_tool", {
      activeTool,
      tool_position: toolPosition
        ? { x: toolPosition[0], y: toolPosition[1], z: toolPosition[2] }
        : { x: 0, y: 0, z: 0 },
      tool_visibility: transform?.toolVisibility ?? 0,
    });
  }

  private setLocalUser(user: UserInfo): void {
    const userId = user.userId || user.email || "local";
    const key = participantKey(userId, this.m_appInstanceId);
    const displayName = nameTagFor(user);
    const initials = initialsForUser(user);
    this.m_localParticipantKey = key;
    debug("setLocalUser", {
      userId,
      key,
      appInstanceId: this.m_appInstanceId,
      appName: this.m_appName,
      activeProjectId: this.m_project.activeProjectId,
      customerId: user.customerId,
      displayName,
      initials,
      colorIndex: this.m_colorIndex,
    });
    this.upsert({
      key,
      userId,
      appInstanceId: this.m_appInstanceId,
      appName: this.m_appName,
      displayName,
      initials,
      email: user.email ?? null,
      projectId: this.m_project.activeProjectId,
      projectCustomerId: this.activeProjectAccountId(),
      color: PARTICIPANT_COLORS[this.m_colorIndex] ?? colorFromString(userId),
      isLocal: true,
      isOnline: true,
      kind: "desktop",
      hasHeadset: false,
      lastSeenAt: Date.now(),
      transform: null,
      // Tri-state null: a re-login upsert must not stomp presenter state
      // learned from the wire (first insert defaults false via `upsert`).
      isPresenter: null,
      // Seed from the SAME live permissions source used by self-election.
      // A permissive constant here let an early shared-state row and the
      // later-resolved permissions model disagree indefinitely.
      canBePresenter: this.localCanBePresenter(),
      currentSceneId: this.m_project.activeSceneId,
      userRole: this.localUserRoleForWire(),
    });
    this.publishLaunchApplication();
    this.publishOpenProject();
    this.reserveColor();
    this.requestSharedState();
  }

  private handleEvent(event: ServerEvent): void {
    const type = event.type.toLowerCase();
    const payload = asRecord(event.payload);
    debug("handleEvent", {
      type,
      keys: payload ? Object.keys(payload).sort() : [],
      activeProjectId: this.m_project.activeProjectId,
    });
    if (!payload) {
      debug("drop event: payload is not object", type);
      return;
    }

    if (matches(type, "receivesharedstate", "sharedstate", "session.sharedstate", "shared_state_snapshot")) {
      // The pending-snapshot gate lifts: housekeeping/self-election may run.
      this.m_awaitingSharedStateUntil = 0;
      const users = arrayField(payload, "ActiveUsers", "activeUsers", "participants");
      // ⚠ ORDER BY LAST HEARTBEAT, ASCENDING — Unity's own comment
      // (`MultiuserAppModel.cs:1256-1262`) says exactly why: "During a
      // reconnection, an app will create a second connection to the server and
      // the server will return two user records with the same app instance id
      // and user id. Ordering by timestamp ensures that the record we put in
      // the participants list has the newest timestamp of the duplicate
      // records." Iterating in server order lets a STALE duplicate land last
      // and win the map slot, taking its `is_presenter` and `project_id` with
      // it. Two tabs of one account reconnecting is precisely that case.
      const serverNowMs = numberField(payload, "srvts", "ServerTimestamp", "serverTimestamp");
      const ordered = [...users].sort(
        (a, b) => (sharedStateHeartbeatMs(a) ?? 0) - (sharedStateHeartbeatMs(b) ?? 0),
      );
      debug("shared state users", { count: ordered.length, serverNowMs });
      for (const raw of ordered) {
        const parsed = participantFromPayload(raw);
        // The server's own liveness verdict for this row. A ghost — a tab that
        // died without sending `close_project` — must arrive OFFLINE, or
        // `ensurePresenter` sees a live presenter that no longer exists and
        // declines to elect anyone.
        const isOnline = sharedStateRowIsLive(raw, serverNowMs);
        this.upsert(parsed && isOnline !== null ? { ...parsed, isOnline } : parsed);
        // "Correct the follower status" (`MultiuserAppModel.cs:1244-1249`):
        // shared-state rows carry the per-user `fu` (FollowingUserKey), the
        // only way a late joiner learns the existing follow graph. Unity
        // applies `Follow(key, fu)` UNCONDITIONALLY — an empty/absent `fu`
        // must CLEAR the entry, or stale follows survive a reconnect.
        const record = asRecord(raw);
        const rowKey = record ? participantKeyFromPayload(record) : null;
        if (rowKey) {
          const followedKey = rawStringField(record ?? {}, "fu", "FollowingUserKey", "followingUserKey");
          if (followedKey) {
            this.followInGraph(rowKey, followedKey);
          } else {
            this.unfollowInGraph(rowKey);
          }
        }
      }
      // AI-1150 retry (a): the snapshot may have just delivered the
      // presenter's `sid`.
      this.retryInitialSceneNavigationIfNeeded();
      return;
    }

    // A heartbeat REFRESHES an existing participant and never creates one —
    // port of `MultiuserAppModel.UpdateLastHeartbeat`, whose own comment says
    // why: a heartbeat from someone we don't have is "probably removed by
    // timeout OR IS SITTING ON THE PROJECT LIST SCREEN", and it carries no
    // project id to tell the two apart (it is a bare `BaseEventData`). Letting
    // it create a row is what put every connected user on the account into
    // this project's roster and scene. `launch_application` / `open_project` /
    // `shared_state` are the create paths, exactly as in Unity.
    if (matches(type, "heartbeat")) {
      this.refreshHeartbeat(payload);
      return;
    }

    if (matches(type, "launchapplication", "launch_application", "openproject", "open_project", "participantjoinsession")) {
      this.upsert(participantFromPayload(payload));
      return;
    }

    if (matches(type, "reserve_color", "reservecolor")) {
      this.applyReservedColor(payload);
      return;
    }

    // Unity splits these two, and with a read-time project filter the split
    // finally matters. `close_project` (`OnServerCloseProject`) CLEARS the
    // participant's project and presenter flag but leaves them online and in
    // the roster — they are still connected, just elsewhere. Marking them
    // offline instead was self-healing in the wrong direction: their next
    // heartbeat flipped them back online, and with no project id recorded
    // they reappeared in whatever project the viewer had open.
    if (matches(type, "closeproject", "close_project")) {
      const key = participantKeyFromPayload(payload);
      if (key) this.clearParticipantProject(key);
      return;
    }

    // `quit_application` / `participant_leave_session`: the app is gone
    // (`RemoveSessionParticipant`).
    if (matches(type, "quitapplication", "quit_application", "participantleavesession")) {
      const key = participantKeyFromPayload(payload);
      if (key) this.markOffline(key);
      return;
    }

    if (matches(type, "changeparticipanttransform", "participant.transform.changed", "participant_transform")) {
      // Unity drops OWN participant_transform echoes: `IgnoreEvent` ignores
      // own same-project events, and participant_transform is deliberately
      // NOT in the exception list (`AWSServerEventListenerModel.cs:263` —
      // the line is commented out upstream). Processing the echo would
      // overwrite the local roster transform with an RTT-stale pose and
      // spend an upsert + two emits per 50 Hz echo for nothing.
      const aid = stringField(payload, "aid", "OriginatingAppInstanceId", "appInstanceId");
      if (isOwnAppInstance(aid)) return;
      const participant = participantFromPayload(payload);
      if (!participant) return;
      // Like heartbeat, a transform NEVER creates a roster row — Unity's
      // `OnServerChangeParticipantTransform` returns when the participant is
      // unknown, and `ParticipantTransformEventData` carries no project id, so
      // a peer streaming poses at 50 Hz from another project would otherwise
      // create an unscopeable row. `open_project` / `shared_state` create.
      if (!this.m_participants.has(participant.key)) {
        debug("drop participant_transform: unknown participant", participant.key);
        return;
      }
      const transform = transformFromPayload(payload);
      this.upsert({ ...participant, transform });
      return;
    }

    // Live active-tool change (`ActiveToolEventData`, tc 0 — wire fields
    // `activeTool` / `tool_position` / `tool_visibility`). Port of
    // `MultiuserSessionModel.OnServerChangeActiveTool`. Own echoes ARE
    // processed (Unity whitelists `active_tool` from the own-event drop —
    // network state is the single source of truth); harmless for self.
    if (matches(type, "active_tool", "activetool")) {
      const projectId = stringField(payload, "projectId", "ProjectId", "project_id");
      if (projectId && this.m_project.activeProjectId && projectId !== this.m_project.activeProjectId) {
        return;
      }
      const key = participantKeyFromPayload(payload);
      const existing = key ? this.m_participants.get(key) : null;
      if (!key || !existing) return;
      const activeTool = numberField(payload, "activeTool", "ActiveTool", "at") ?? 0;
      const toolPosition = vec3From(
        payload.tool_position ?? payload.ToolPosition ?? payload.toolPosition,
      );
      const toolVisibility = numberField(payload, "tool_visibility", "ToolVisibility", "toolVisibility");
      // Deliberate SNAP of the tool point (Unity passes interpolate:false
      // on a tool switch) — the continuous point keeps riding `tp`/`tv`.
      const transform = existing.transform
        ? {
            ...existing.transform,
            ...(toolPosition ? { toolPosition } : {}),
            ...(toolVisibility !== null ? { toolVisibility } : {}),
          }
        : existing.transform;
      const next = { ...existing, activeTool, transform, lastSeenAt: Date.now() };
      this.m_participants.set(key, next);
      if (next.transform) this.onParticipantTransformChanged.emit(next);
      this.onParticipantsChanged.emit(this.participants);
      return;
    }

    // A participant navigated to a scene (`TargetSceneEventData`, tc 39).
    // Port of `MultiuserAppModel.OnServerLoadScene` (roster `sid` update) +
    // `MultiuserSessionModel.OnServerLoadScene` (follow ONLY presenters).
    if (matches(type, "load_scene", "loadscene")) {
      this.handleLoadScene(payload);
      return;
    }

    // Presenter handoff (`presenter_change` tc 26 — `presenterId` is the
    // participant key `"{uid}:{aid}"`). Port of
    // `MultiuserAppModel.OnServerChangePresenter` + `UpdateCurrentPresenter`.
    // OWN echoes are processed too — the network state is the single source
    // of truth (`AWSServerEventListenerModel.cs:200-227`).
    if (matches(type, "presenter_change", "presenterchange")) {
      this.handlePresenterChange(payload);
      return;
    }

    // Follow graph sync (`follower_change` tc 17 — note the wire type is
    // "follower_change", not "follow_change"). Port of
    // `MultiuserAppModel.OnServerChangeFollower`: the FOLLOWER is the event
    // sender; empty `followedUserKey` means unfollow.
    if (matches(type, "follower_change", "followerchange", "follow_change", "followchange")) {
      const followerKey = participantKeyFromPayload(payload);
      if (!followerKey) return;
      const followedKey = stringField(payload, "followedUserKey", "FollowedUserKey", "followed_user_key");
      if (followedKey) {
        this.followInGraph(followerKey, followedKey);
      } else {
        this.unfollowInGraph(followerKey);
        // Own stop-echo (the follower is always the sender, so a
        // follower_change about the local key can only be our own event).
        // Apply it to local follow state only when "" is still the latest
        // published intent — a stop echo landing after a newer
        // `followUser(B)` publish is stale and must not cancel the B follow
        // (see `m_lastPublishedFollowKey`).
        if (
          followerKey === this.m_localParticipantKey
          && this.m_lastPublishedFollowKey === ""
        ) {
          this.endLocalFollow(false);
        }
      }
      return;
    }

    // Follow invitation (`ask_to_follow` tc 5). Port of
    // `MultiuserAppModel.OnAsktoFollow`: empty `asktoFollowUserKey` addresses
    // everyone ("Gather everyone"), otherwise only the named participant.
    if (matches(type, "ask_to_follow", "asktofollow")) {
      const askerKey = participantKeyFromPayload(payload);
      const self = this.m_localParticipantKey;
      if (!askerKey || !self || askerKey === self) return;
      const projectId = stringField(payload, "projectId", "ProjectId", "project_id");
      if (projectId && projectId !== this.m_project.activeProjectId) return;
      const asker = this.m_participants.get(askerKey);
      if (!asker) return;
      const targetKey = stringField(payload, "asktoFollowUserKey", "AsktoFollowUserKey") ?? "";
      // Unity guard: drop when the addressed user already follows the asker.
      if (targetKey && this.m_followerToFollowed.get(targetKey) === askerKey) return;
      if ((targetKey === "" || targetKey === self)
        && this.m_followerToFollowed.get(self) !== askerKey) {
        this.onAskedToFollow.emit(asker);
      }
      return;
    }
  }

  /** Port of `OnServerChangePresenter` + `UpdateCurrentPresenter`. */
  private handlePresenterChange(payload: Record<string, unknown>): void {
    const presenterKey = stringField(payload, "presenterId", "PresenterId", "presenter_id");
    if (!presenterKey) return;
    // Unity drops presenter events whose sender is unknown (verbatim
    // parity) — but a permanently-missed change would leave a two-presenter
    // split that nothing heals (the web, unlike Unity, has no
    // auto-participant-creation on arbitrary events). Re-request the shared
    // state so the authoritative `is_presenter` rows reconverge the roster;
    // the pending-snapshot gate doubles as the spam guard.
    const senderKey = participantKeyFromPayload(payload);
    const sender = senderKey ? this.m_participants.get(senderKey) ?? null : null;
    if (!senderKey || !sender) {
      debug("presenter_change ignored: sender unknown", senderKey);
      if (Date.now() >= this.m_awaitingSharedStateUntil) {
        this.requestSharedState({ gateElection: false });
      }
      return;
    }
    const nominatedPresenter = this.m_participants.get(presenterKey) ?? null;
    const activeProjectId = this.m_project.activeProjectId;
    const activeProjectAccountId = this.activeProjectAccountId();
    if (
      !nominatedPresenter
      || !nominatedPresenter.isOnline
      || !isParticipantInSession(sender, activeProjectId, activeProjectAccountId)
      || !isParticipantInSession(nominatedPresenter, activeProjectId, activeProjectAccountId)
    ) {
      debug("presenter_change ignored: outside active project", {
        presenterKey,
        sender: summarizeParticipant(sender),
        nominatedPresenter: nominatedPresenter ? summarizeParticipant(nominatedPresenter) : null,
        activeProjectId,
        activeProjectAccountId,
      });
      return;
    }
    // Rules (`MultiuserAppModel.cs:1704-1719`): changes can't be processed
    // faster than every 3 s — this resolves simultaneous self-elections.
    const now = Date.now();
    if (now - this.m_lastPresenterChangeProcessedAt < K_MIN_MS_BETWEEN_PRESENTER_CHANGES) {
      debug("presenter_change ignored: rate limited");
      return;
    }
    // `UpdateCurrentPresenter`: promote the (online) nominee, demote everyone
    // else — the single-presenter invariant is enforced here.
    const oldPresenterKey = this.getPresenter()?.key ?? null;
    for (const participant of this.m_participants.values()) {
      if (!isParticipantInSession(participant, activeProjectId, activeProjectAccountId)) continue;
      const isPresenter = participant.key === presenterKey;
      if (participant.isPresenter !== isPresenter) {
        this.m_participants.set(participant.key, { ...participant, isPresenter });
      }
    }
    const newPresenterKey = presenterKey;
    this.m_lastPresenterChangeProcessedAt = now;
    if (oldPresenterKey !== newPresenterKey) {
      debug("presenter changed", { oldPresenterKey, newPresenterKey });
      this.onParticipantsChanged.emit(this.participants);
      this.onPresenterChanged.emit({ oldPresenterKey, newPresenterKey });
      // `OnAppModelPresenterChanged` → `TryNavigateToScenePresenterIsIn`:
      // a handoff pulls everyone into the presenter's current scene.
      this.navigateToPresenterScene();
    }
  }

  /** Port of `MultiuserSessionModel.TryNavigateToScenePresenterIsIn`. */
  private navigateToPresenterScene(): void {
    const presenter = this.getPresenter();
    if (!presenter?.currentSceneId) return;
    if (presenter.projectId && presenter.projectId !== this.m_project.activeProjectId) return;
    if (this.m_project.activeSceneId === presenter.currentSceneId) return;
    debug("navigating to presenter scene", presenter.currentSceneId);
    void this.m_project.setActiveScene(presenter.currentSceneId).catch((err: unknown) => {
      console.warn("[MultiuserSessionModel] presenter scene navigation failed", err);
    });
  }

  /**
   * AI-1150 retry body (`RetryInitialSceneNavigationIfNeeded`): while the
   * one-shot join flag is armed, join the remote presenter's scene as soon
   * as its `sid` is known. The resulting scene change clears the flag via
   * the `onActiveSceneChanged` subscription.
   */
  private retryInitialSceneNavigationIfNeeded(): void {
    if (!this.m_needsInitialSceneNavigation) return;
    if (!this.m_project.activeProjectId) return;
    const presenter = this.getPresenter();
    if (!presenter || presenter.isLocal || !presenter.currentSceneId) return;
    if (presenter.projectId && presenter.projectId !== this.m_project.activeProjectId) return;
    if (this.m_project.activeSceneId === presenter.currentSceneId) {
      this.m_needsInitialSceneNavigation = false;
      return;
    }
    debug("initial-scene self-heal: joining presenter scene", presenter.currentSceneId);
    this.navigateToPresenterScene();
  }

  /** Port of `MultiuserAppModel.Follow` — graph write + change event. */
  private followInGraph(followerKey: string, newFollowedKey: string): void {
    const oldFollowedKey = this.m_followerToFollowed.get(followerKey) ?? "";
    this.m_followerToFollowed.set(followerKey, newFollowedKey);
    if (oldFollowedKey !== newFollowedKey) {
      this.onFollowChanged.emit({ followerKey, oldFollowedKey, newFollowedKey });
    }
  }

  /** Port of `MultiuserAppModel.Unfollow`. */
  private unfollowInGraph(followerKey: string): void {
    const oldFollowedKey = this.m_followerToFollowed.get(followerKey);
    if (oldFollowedKey === undefined) return;
    this.m_followerToFollowed.delete(followerKey);
    this.onFollowChanged.emit({ followerKey, oldFollowedKey, newFollowedKey: "" });
  }

  private handleLoadScene(payload: Record<string, unknown>): void {
    const sceneId = stringField(payload, "sceneId", "SceneID", "SceneId");
    if (!sceneId) return;
    const aid = stringField(payload, "aid", "OriginatingAppInstanceId", "appInstanceId");
    if (isOwnAppInstance(aid)) return;

    // Roster bookkeeping first — Unity tracks every participant's scene even
    // when the project doesn't match (`load_scene` is never listener-filtered).
    const key = participantKeyFromPayload(payload);
    const sender = key ? this.m_participants.get(key) ?? null : null;
    if (key && sender) {
      this.m_participants.set(key, { ...sender, currentSceneId: sceneId, lastSeenAt: Date.now() });
      this.onParticipantsChanged.emit(this.participants);
    }

    // Follow leg: only a PRESENTER's navigation moves us, and only within
    // the active project (`MultiuserSessionModel.cs:726-756`).
    const projectId = stringField(payload, "projectId", "ProjectId", "project_id");
    if (!projectId || projectId !== this.m_project.activeProjectId) {
      // AI-1150 retry (c): the roster `sid` update above may have been the
      // presenter's — even for load_scene events we don't follow.
      this.retryInitialSceneNavigationIfNeeded();
      return;
    }
    if (!sender?.isPresenter) {
      this.retryInitialSceneNavigationIfNeeded();
      return;
    }
    if (this.m_project.activeSceneId === sceneId) return;
    debug("following presenter load_scene", { sceneId, presenter: sender.key });
    void this.m_project.setActiveScene(sceneId).catch((err: unknown) => {
      console.warn("[MultiuserSessionModel] presenter follow failed", err);
    });
  }

  private tick(): void {
    this.publishHeartbeat();
  }

  /**
   * 1 Hz housekeeping — port of Unity `MultiuserSessionModel.CoHousekeeping`:
   * idle-participant sweep (`TryRemoveIdleSessionParticipants`) followed by
   * presenter self-election (`EnsurePresenter`).
   */
  private housekeeping(): void {
    const now = Date.now();
    let changed = false;
    for (const participant of this.m_participants.values()) {
      if (participant.isLocal || !participant.isOnline) continue;
      if (now - participant.lastSeenAt <= OFFLINE_AFTER_MS) continue;
      debug("marking participant offline by timeout", summarizeParticipant(participant));
      this.m_participants.set(participant.key, { ...participant, isOnline: false });
      changed = true;
      this.handleParticipantGone(participant.key);
    }
    if (changed) this.onParticipantsChanged.emit(this.participants);
    this.ensurePresenter();
    // AI-1150 retry (d): 1 Hz safety net for missed/out-of-order events.
    this.retryInitialSceneNavigationIfNeeded();
  }

  /**
   * Port of Unity `MultiuserSessionModel.EnsurePresenter`: when no online,
   * presenter-capable participant holds the flag, nominate SELF. Simultaneous
   * self-elections are resolved receiver-side by the 3 s rate limit — first
   * event through the relay wins on every client, including this one (the
   * flag only flips when our own echo returns).
   */
  private ensurePresenter(): void {
    if (this.m_events.state !== "connected") return;
    if (!this.m_project.activeProjectId) return;
    // Never self-elect while a shared-state snapshot is pending — the
    // legitimate presenter may simply not have arrived in the roster yet
    // (Unity stops housekeeping outright for this window).
    if (Date.now() < this.m_awaitingSharedStateUntil) return;
    const local = this.localParticipant();
    if (!local) return;
    if (!isParticipantInSession(local, this.m_project.activeProjectId, this.activeProjectAccountId())) return;
    // Unity `UpdateCurrentPresenter` scans `GetSessionParticipants(projectId,
    // accountId, excludeRemote: true, ...)` — scoping matters both ways here:
    // a presenter in ANOTHER project must not suppress this project's
    // election, and a Campfire Remote peer can't hold presentership.
    for (const participant of this.getActiveProjectParticipants()) {
      if (participant.isPresenter && participant.canBePresenter) return;
    }
    // Unity `MultiuserSessionModel.cs:488` — you may only nominate yourself if
    // you are presenter-ELIGIBLE. This was the gap the old TODO named: a
    // client that must never present used to self-nominate, and Unity peers
    // accept it. A project's own creator is always eligible, so the solo case
    // still self-elects.
    if (!this.localCanBePresenter()) {
      debug("ensurePresenter: no presenter, but this user may not present");
      return;
    }
    debug("ensurePresenter: no presenter, nominating self");
    this.publish("presenter_change", { presenterId: local.key });
  }

  /**
   * Shared cleanup when a participant leaves the session (close_project,
   * leave event, or heartbeat timeout): Unity `OnServerCloseProject` clears
   * the presenter flag and `Unfollow`s the leaver; a follower whose target
   * vanished cancels its follow (`AudienceFollowViewModel.OnParticipantLeaveSession`).
   */
  private handleParticipantGone(key: string): void {
    const participant = this.m_participants.get(key);
    if (participant?.isPresenter) {
      this.m_participants.set(key, { ...participant, isPresenter: false });
      this.onPresenterChanged.emit({ oldPresenterKey: key, newPresenterKey: null });
    }
    this.unfollowInGraph(key);
    if (this.m_localFollowedKey === key) {
      this.endLocalFollow(true);
    }
  }

  /**
   * The permissions model, resolved lazily — this model is constructed in the
   * core pass and cannot take it as a constructor argument.
   */
  private permissions(): IPermissionsModel | null {
    try {
      return Ioc.resolve<IPermissionsModel>(PERMISSIONS_MODEL);
    } catch {
      return null;
    }
  }

  /**
   * The local user's role AS UNITY ENCODES IT on the wire — an INT, from the
   * EFFECTIVE account role (`MultiuserSessionModel.cs:594-608`, re-stamped at
   * `:625`).
   *
   * ⚠ NOTE AN OWNER LEGITIMATELY BROADCASTS AS `Editor`. `SharedUserRoleToAccountRole`
   * collapses Owner→Writer, so `EffectiveRole` can never be Owner and Unity's
   * own switch maps Writer→Editor. Do NOT "fix" this to publish Owner — every
   * desktop peer expects the upstream encoding, and `CanAssignPresenter`
   * reads presenter ELIGIBILITY rather than this field anyway.
   */
  private localUserRoleForWire(): number {
    const permissions = this.permissions();
    if (!permissions) return SHARED_USER_ROLE.Viewer;
    return accountRoleToSharedUserRole(permissions.effectiveRole);
  }

  /** `UserInfo.CanBePresenter` for the open project; false when unresolved. */
  private localCanBePresenter(): boolean {
    return this.permissions()?.canBePresenter() ?? false;
  }

  /**
   * Keep the local roster row aligned with the permissions model.
   *
   * Role resolution is asynchronous. Previously `setLocalUser` seeded
   * `canBePresenter: true`, while the first presence/snapshot exchange could
   * write the then-unresolved false value back over it. Once permissions
   * resolved, the wire payload changed but the local row did not. If our own
   * presenter echo then set `isPresenter: true`, `ensurePresenter` still saw
   * `isPresenter && canBePresenter` as false and nominated us again every
   * housekeeping tick. Unity peers accepted the first event and rate-limited
   * the one-per-second repeats.
   */
  private syncLocalPermissions(): void {
    const local = this.localParticipant();
    if (!local) return;
    const canBePresenter = this.localCanBePresenter();
    const userRole = this.localUserRoleForWire();
    if (
      local.canBePresenter === canBePresenter
      && local.userRole === userRole
    ) {
      return;
    }
    this.m_participants.set(local.key, {
      ...local,
      canBePresenter,
      userRole,
      lastSeenAt: Date.now(),
    });
    this.onParticipantsChanged.emit(this.participants);
    // Heartbeats are BaseEventData on Unity and do not update roster role
    // fields. Re-announce the open-project payload so peers receive the newly
    // resolved eligibility/role immediately rather than retaining startup
    // defaults.
    this.publishOpenProject();
  }

  private publishHeartbeat(): void {
    debug("publishHeartbeat");
    this.publish("heartbeat", {});
  }

  private handleConnected(): void {
    debug("handleConnected", {
      activeProjectId: this.m_project.activeProjectId,
      localParticipantKey: this.m_localParticipantKey,
    });
    this.publishLaunchApplication();
    this.publishOpenProject();
    this.reserveColor();
    this.requestSharedState();
  }

  private handleActiveProjectChanged(): void {
    // `onActiveProjectChanged` also fires for in-place project mutations
    // (e.g. an inbound `update_project` rename) — only an actual project
    // SWITCH should reset the roster and re-announce presence.
    if (this.m_project.activeProjectId === this.m_lastProjectId) return;
    if (this.m_lastProjectId && this.m_project.activeProjectId !== this.m_lastProjectId) {
      this.sendPendingProjectClose();
    }
    this.m_lastProjectId = this.m_project.activeProjectId;
    this.m_openProjectIdPendingClose = this.m_lastProjectId;
    this.m_openProjectStartedAt = this.m_openProjectIdPendingClose ? Date.now() : 0;
    debug("handleActiveProjectChanged", {
      previousCount: this.m_participants.size,
      activeProjectId: this.m_project.activeProjectId,
    });
    // AI-1150: arm the join-time presenter-scene preference for the new
    // project (retries run off shared state / load_scene / housekeeping
    // until the first post-load scene change resolves the intent).
    this.m_needsInitialSceneNavigation = Boolean(this.m_project.activeProjectId);
    this.m_initialSceneEmitPending = Boolean(this.m_project.activeProjectId);
    // NOTE: `m_localActiveTool` is deliberately NOT reset here. The toolbar
    // keeps its selection across a project switch and its change event won't
    // re-fire, so zeroing the cache would make the presence `at` field (and
    // every peer's tool-beam gate) under-report the real tool until the user
    // switched tools twice.
    // Leaving the project ends any local follow (silently — Unity's
    // `close_project` clears it server-side) and resets the follow graph
    // (it is per-session-roster state, re-seeded by shared state). The
    // presenter rate-limit stamp resets too so the new project's first
    // presenter_change can't be dropped by a change from the old one.
    this.endLocalFollow(false);
    this.m_followerToFollowed.clear();
    this.m_lastPresenterChangeProcessedAt = 0;
    const local = this.localParticipant();
    this.m_participants.clear();
    if (local) {
      this.m_participants.set(local.key, {
        ...local,
        projectId: this.m_project.activeProjectId,
        projectCustomerId: this.activeProjectAccountId(),
        lastSeenAt: Date.now(),
      });
    }
    this.onParticipantsChanged.emit(this.participants);
    this.publishOpenProject();
    this.reserveColor();
    this.requestSharedState();
  }

  private publishLaunchApplication(): void {
    const local = this.localParticipant();
    if (!local) return;
    debug("publishLaunchApplication", summarizeParticipant(local));
    this.publish("launch_application", {
      appName: this.m_appName,
      app_name: this.m_appName,
      AppName: this.m_appName,
      device_id: WEB_DEVICE_ID,
      DeviceId: WEB_DEVICE_ID,
      DeviceType: "Web",
    });
  }

  private publishOpenProject(): void {
    const local = this.localParticipant();
    if (!local || !this.m_project.activeProjectId) {
      debug("skip publishOpenProject", {
        hasLocal: Boolean(local),
        activeProjectId: this.m_project.activeProjectId,
      });
      return;
    }
    debug("publishOpenProject", {
      participant: summarizeParticipant(local),
      customerId: this.m_auth.currentUser?.customerId,
      colorIndex: this.m_colorIndex,
    });
    this.publish("open_project", {
      ...this.desktopParticipantPayload(local),
      userTag: local.displayName,
      name: local.displayName,
      initials: local.initials,
      Initials: local.initials,
      ci: this.m_colorIndex,
      CustomerId: this.m_auth.currentUser?.customerId,
      customerId: this.m_auth.currentUser?.customerId,
      ProjectName: this.m_project.activeProject?.name,
      projectName: this.m_project.activeProject?.name,
      // ⚠ `ur`, an INT — Unity's serialized field name
      // (`OpenProjectEventData.cs:112`). The web previously sent
      // `UserRole`/`userRole` as a STRING, which JsonUtility cannot parse into
      // a `SharedUserRole`, so every desktop peer silently defaulted the field
      // to 0 — and 0 is `Owner`. Every web participant therefore appeared to
      // Unity clients as an owner with presenter rights.
      ur: this.localUserRoleForWire(),
      OriginatingDeviceId: WEB_DEVICE_ID,
    });
  }

  private handleBeginUnloadProject(projectId: string): void {
    if (projectId !== this.m_openProjectIdPendingClose) return;
    this.sendPendingProjectClose();
  }

  private handleActiveSceneChanged(sceneId: string | null): void {
    const local = this.localParticipant();
    if (local && local.currentSceneId !== sceneId) {
      this.m_participants.set(local.key, {
        ...local,
        currentSceneId: sceneId,
        projectId: this.m_project.activeProjectId,
        projectCustomerId: this.activeProjectAccountId(),
        lastSeenAt: Date.now(),
      });
      this.onParticipantsChanged.emit(this.participants);
    }
    if (sceneId === this.m_loadedSceneIdPendingUnload) return;
    this.sendPendingSceneUnload();
    if (!sceneId || !this.m_project.activeProjectId) return;
    this.m_loadedSceneIdPendingUnload = sceneId;
    this.m_loadedSceneStartedAt = Date.now();
  }

  private sendPendingProjectClose(): void {
    const projectId = this.m_openProjectIdPendingClose;
    if (!projectId) return;
    this.sendPendingSceneUnload();
    const duration = this.m_openProjectStartedAt > 0
      ? Math.max(0, Math.round((Date.now() - this.m_openProjectStartedAt) / 1000))
      : 0;
    this.publish("close_project", {
      ProjectId: projectId,
      projectId,
      project_id: projectId,
      Duration: duration,
      duration,
    });
    this.m_openProjectIdPendingClose = null;
    this.m_openProjectStartedAt = 0;
  }

  private sendPendingSceneUnload(): void {
    const projectId = this.m_openProjectIdPendingClose;
    const sceneId = this.m_loadedSceneIdPendingUnload;
    if (!projectId || !sceneId) return;
    const duration = this.m_loadedSceneStartedAt > 0
      ? Math.max(0, Math.round((Date.now() - this.m_loadedSceneStartedAt) / 1000))
      : 0;
    this.publish("unload_scene", {
      ProjectId: projectId,
      projectId,
      project_id: projectId,
      SceneId: sceneId,
      SceneID: sceneId,
      sceneId,
      Duration: duration,
      duration,
    });
    this.m_loadedSceneIdPendingUnload = null;
    this.m_loadedSceneStartedAt = 0;
  }

  private reserveColor(): void {
    const local = this.localParticipant();
    if (!local || !this.m_project.activeProjectId) {
      debug("skip reserveColor", {
        hasLocal: Boolean(local),
        activeProjectId: this.m_project.activeProjectId,
      });
      return;
    }
    debug("reserveColor", {
      colorIndex: this.m_colorIndex,
      participant: summarizeParticipant(local),
    });
    this.publish("reserve_color", {
      color_index: this.m_colorIndex,
      ci: this.m_colorIndex,
    });
  }

  /**
   * Ask the server for the session roster.
   *
   * `gateElection` distinguishes the two reasons this is called, and the
   * distinction is load-bearing. A JOIN (connect / project switch / local user
   * set) legitimately suppresses presenter self-election until the response
   * arrives, because the room's real presenter may simply not be in our roster
   * yet. A mid-session REPAIR (an unknown-sender `presenter_change`, a
   * heartbeat from someone we don't have) must NOT: those fire while peers are
   * nominating once a second, and each one re-arming a 10 s gate is a livelock
   * — `ensurePresenter` never gets a tick to run in, so nobody is ever elected
   * and the events that caused the repair keep arriving. Unity has no such
   * hazard: its suspension (`OnAppModelBeginRequestSharedState` ->
   * `StopMultiuserSession`) is lifted by the RESPONSE, and it never re-requests
   * from the presenter path.
   *
   * The heartbeat goes FIRST, verbatim from `MultiuserAppModel
   * .RequestSharedStateAsync` (`:1075`) and for its stated reason: "Send extra
   * heartbeat to prevent the current user from being considered a crashed in
   * the OnReceiveSharedState event handler." Now that snapshot rows carry the
   * server's liveness verdict (see `sharedStateRowIsLive`), asking before
   * announcing ourselves would have the server report US as idle.
   */
  private requestSharedState(options: { gateElection?: boolean } = {}): void {
    debug("requestSharedState", {
      customerId: this.m_auth.currentUser?.customerId,
      activeProjectId: this.m_project.activeProjectId,
      gateElection: options.gateElection !== false,
    });
    this.publishHeartbeat();
    if (options.gateElection !== false) {
      this.m_awaitingSharedStateUntil = Date.now() + SHARED_STATE_WAIT_MS;
    }
    this.publish("shared_state_snapshot", {
      extCustId: this.m_auth.currentUser?.customerId,
    });
  }

  private publish(type: string, payload: Record<string, unknown>): void {
    const local = this.localParticipant();
    if (!local) {
      debug("skip publish: no local participant", type);
      return;
    }
    debug("publish", {
      type,
      activeProjectId: this.m_project.activeProjectId,
      local: summarizeParticipant(local),
      payloadKeys: Object.keys(payload).sort(),
    });
    this.m_events.send({
      type,
      payload: {
        ...this.desktopParticipantPayload(local),
        ...payload,
        ProjectId: this.m_project.activeProjectId,
        projectId: this.m_project.activeProjectId,
        project_id: this.m_project.activeProjectId,
        // Unity `OpenProjectEventData.CustomerId` is the PROJECT's account —
        // `OnServerOpenProject` assigns it straight to
        // `participantData.ProjectCustomerId` (`MultiuserAppModel.cs:1427`).
        // Sending the sender's own account here would poison a Unity peer's
        // roster on a cross-account share; keep it equal to `act_id`.
        CustomerId: this.activeProjectAccountId(),
        customerId: this.activeProjectAccountId(),
        OriginatingUserId: local.userId,
        OriginatingAppInstanceId: this.m_appInstanceId,
        uid: local.userId,
        aid: this.m_appInstanceId,
        DisplayName: local.displayName,
        userTag: local.displayName,
        name: local.displayName,
        initials: local.initials,
        Initials: local.initials,
        color_index: this.m_colorIndex,
        ci: this.m_colorIndex,
        Email: local.email,
        Color: local.color,
      },
    });
  }

  private desktopParticipantPayload(local: MultiuserParticipant): Record<string, unknown> {
    return {
      appName: this.m_appName,
      app_name: this.m_appName,
      AppName: this.m_appName,
      device_id: WEB_DEVICE_ID,
      DeviceId: WEB_DEVICE_ID,
      // Live XR-session state, not a constant: peers pick the mannequin-head
      // representation for desktop+HasHeadset (Unity `CreateNetworkUser`).
      hd: this.m_localHasHeadset,
      HasHeadset: this.m_localHasHeadset,
      metaQuestID: 0,
      ctrlTyp: "",
      // The CURRENT tool, not a hardcoded 0 — a heartbeat repeating the
      // live value is benign (unlike `is_presenter`/`fu`, which stay
      // deliberately absent; see the stale-echo note below).
      at: this.m_localActiveTool,
      battery_level: 0,
      // The REAL value now. It was hardcoded true while the web had no account
      // role, which let a client that must never present nominate itself —
      // Unity peers accept the nomination.
      p: this.localCanBePresenter(),
      CanBePresenter: this.localCanBePresenter(),
      // Deliberately NO `is_presenter` / `fu` here: Unity never sends them in
      // client→server presence events (heartbeat is a bare BaseEventData) —
      // the server derives shared-state rows from `presenter_change` /
      // `follower_change`. Stamping them here creates a stale-echo channel:
      // a heartbeat sent just before a handoff echoes back AFTER it and
      // re-promotes the old presenter (observed live on the dev relay).
      user_id: local.userId,
      sid: this.m_project.activeSceneId ?? "",
      // Unity `SessionParticipantData.ProjectCustomerId` — the account that
      // OWNS the open project, not the sender's own account. They differ on a
      // cross-account share, and both sides of `IsInSession`'s account term
      // must mean the same thing or a legitimate guest is filtered out.
      act_id: this.activeProjectAccountId() ?? "",
      t: transformToWire(publishableTransform(local.transform)),
      transform: transformToWire(publishableTransform(local.transform)),
    };
  }

  private localParticipant(): MultiuserParticipant | null {
    return this.m_localParticipantKey
      ? this.m_participants.get(this.m_localParticipantKey) ?? null
      : null;
  }

  private upsert(participant: UpsertParticipant | null): void {
    if (!participant) {
      debug("upsert skipped: null participant");
      return;
    }
    if (participant.isLocal && this.m_localParticipantKey && participant.key !== this.m_localParticipantKey) {
      debug("upsert skipped: conflicting local participant", summarizeParticipant(participant));
      return;
    }
    const existing = this.m_participants.get(participant.key);
    // A payload WITHOUT a pose keeps the one we hold. Unity's
    // `SessionParticipantInfo.Transform` is a field on a long-lived record
    // that only real poses are ever written to; the web rebuilds the record
    // from each payload, so `open_project` / `launch_application` / a
    // pose-less shared-state row would otherwise WIPE a peer's pose. That
    // wipe is what made "Follow" report a peer as not tracking while they
    // stood there in the scene: senders change-gate at 1 cm / 5°, so nothing
    // refills the pose until the peer next moves.
    //
    // AI-1188: an exactly-(0,0,0) position is the "uninitialized" sentinel
    // (a participant mid-load can broadcast it). Applying it would snap the
    // avatar to scene center — keep the last known-good pose instead.
    // Quest-controller keys are exempt: their transforms are relative and
    // legitimately pass through the origin.
    const incomingTransform = participant.transform ?? null;
    let transform = incomingTransform ?? existing?.transform ?? null;
    if (
      !participant.isLocal
      && incomingTransform !== null
      && isZeroPosition(incomingTransform.position)
      && !isQuestControllerKey(participant.key)
    ) {
      transform = existing?.transform ?? null;
    }
    const isLocal = existing?.isLocal === true || participant.isLocal;
    const next: MultiuserParticipant = {
      ...participant,
      transform,
      // Device identity rides the ROSTER payloads only, so like the presence
      // fields below it merges rather than overwrites — a `participant_transform`
      // carries neither `app_name` nor `hd`, and resetting them on every pose
      // update dropped tracked peers to `unknown`/no-headset, which both
      // re-renders their avatar as the fallback screen frame and (via
      // `participantPoseIsTracked`) changes how `isParticipantTracking` judges
      // them.
      appName: participant.appName ?? existing?.appName ?? null,
      kind: participant.kind ?? existing?.kind ?? "unknown",
      hasHeadset: participant.hasHeadset ?? existing?.hasHeadset ?? false,
      displayName: chooseDisplayName(existing, participant),
      initials: chooseInitials(existing, participant),
      color: chooseColor(existing, participant),
      isLocal,
      // Every other path passes `true`; only a `shared_state_snapshot` row can
      // arrive false, carrying the SERVER's verdict (`sharedStateRowIsLive`).
      // The LOCAL row is exempt unconditionally: `participants`' contract is
      // that "You" is never hidden, and a snapshot that judges us idle —
      // reachable whenever our own heartbeat has not landed yet — would empty
      // our roster and, because a non-presenter cannot author, disable the
      // whole editor.
      isOnline: existing?.isLocal === true || participant.isLocal
        ? true
        : participant.isOnline,
      lastSeenAt: Date.now(),
      // Tri-state merge: payloads without the field keep the known value
      // (heartbeats / transform events don't repeat presenter or scene).
      isPresenter: participant.isPresenter ?? existing?.isPresenter ?? false,
      // Local authorization has one source of truth: the permissions model.
      // Never let a delayed presence echo/shared-state row restore the
      // fail-closed startup value after role resolution. Remote peers remain
      // wire-authoritative and default true per the documented web deviation.
      canBePresenter: isLocal
        ? this.localCanBePresenter()
        : participant.canBePresenter ?? existing?.canBePresenter ?? true,
      currentSceneId: participant.currentSceneId ?? existing?.currentSceneId ?? null,
      activeTool: participant.activeTool ?? existing?.activeTool ?? 0,
      // A peer's role. Absent keeps the known value; nothing gates on it, so
      // an unresolved role is left `undefined` rather than invented.
      userRole: isLocal
        ? this.localUserRoleForWire()
        : participant.userRole ?? existing?.userRole,
      // `undefined` = the payload had no project field (heartbeats, transform
      // events) → keep what we know. `null` = explicitly in no project
      // (`close_project`) → clear it. Note this must test `undefined`, not
      // falsiness: `?? existing` would resurrect a cleared project on the
      // very next heartbeat.
      projectId: participant.projectId !== undefined
        ? participant.projectId
        : existing?.projectId ?? null,
      projectCustomerId: participant.projectCustomerId !== undefined
        ? participant.projectCustomerId
        : existing?.projectCustomerId ?? null,
    };
    // Join = fresh non-local insert OR offline→online flip (mirrors Unity
    // `ParticipantJoinSession`'s open_project + heartbeat-revival raises).
    const isJoin =
      !next.isLocal
      && next.isOnline
      && !isQuestControllerKey(next.key)
      && (!existing || existing.isOnline === false);
    this.m_participants.set(participant.key, next);
    debug("upsert participant", {
      participant: summarizeParticipant(next),
      count: this.m_participants.size,
      all: this.participants.map(summarizeParticipant),
    });
    // Only a pose that actually ARRIVED is a transform change. Now that a
    // pose-less payload carries the previous one forward, identity is the
    // test: `transformFromPayload` builds a fresh object per event, so a real
    // update always differs, and a merge never re-drives the follow camera.
    if (next.transform && next.transform !== existing?.transform) {
      this.onParticipantTransformChanged.emit(next);
    }
    this.onParticipantsChanged.emit(this.participants);
    if (isJoin) this.onParticipantJoined.emit(next);
  }

  /**
   * Port of `MultiuserAppModel.UpdateLastHeartbeat` (`:1180`) — refresh an
   * EXISTING participant's liveness, and return for one we don't have.
   *
   * A heartbeat is a bare `BaseEventData`: no project id, no app name, no
   * display name. Creating a row from it produced a participant that belonged
   * to no project and could never be scoped to one, which is precisely how
   * everyone connected to the account ended up in the open project's roster
   * and scene.
   *
   * Unity has the resync below at this exact site but commented out, because
   * its roster is never emptied. The web clears on project switch, so the
   * "we're missing someone" case is reachable and worth repairing — throttled,
   * since otherwise every heartbeat from an out-of-project peer (one every
   * 5 s, per client) would request a fresh snapshot.
   */
  private refreshHeartbeat(payload: Record<string, unknown>): void {
    const key = participantKeyFromPayload(payload);
    if (!key) return;
    const existing = this.m_participants.get(key);
    if (!existing) {
      if (isOwnAppInstance(stringField(payload, "aid", "OriginatingAppInstanceId"))) return;
      const now = Date.now();
      if (now - this.m_lastUnknownHeartbeatResyncAt < UNKNOWN_HEARTBEAT_RESYNC_MS) return;
      this.m_lastUnknownHeartbeatResyncAt = now;
      debug("heartbeat from unknown participant, requesting shared state", key);
      this.requestSharedState({ gateElection: false });
      return;
    }
    const revived = !existing.isOnline;
    const next: MultiuserParticipant = { ...existing, isOnline: true, lastSeenAt: Date.now() };
    this.m_participants.set(key, next);
    if (revived) {
      debug("participant came back online", summarizeParticipant(next));
      this.onParticipantsChanged.emit(this.participants);
      // Unity raises `ParticipantJoinSession` on the offline→online flip.
      if (!next.isLocal && !isQuestControllerKey(key)) this.onParticipantJoined.emit(next);
    }
  }

  /**
   * Port of `MultiuserAppModel.OnServerCloseProject` (`:1487`): the
   * participant LEFT THE PROJECT but is still connected — clear their project,
   * owning account, and presenter flag, and unfollow them, while leaving them
   * online in the global roster (the projects grid still shows them, and their
   * next `open_project` moves them to wherever they went).
   */
  private clearParticipantProject(key: string): void {
    const existing = this.m_participants.get(key);
    if (!existing || existing.isLocal) {
      debug("clearParticipantProject skipped", { key, found: Boolean(existing) });
      return;
    }
    debug("clearParticipantProject", summarizeParticipant(existing));
    // Presenter flag + follow-graph cleanup before the project clears
    // (re-election follows via `ensurePresenter`).
    this.handleParticipantGone(key);
    const current = this.m_participants.get(key) ?? existing;
    this.m_participants.set(key, {
      ...current,
      projectId: null,
      projectCustomerId: null,
      lastSeenAt: Date.now(),
    });
    this.onParticipantsChanged.emit(this.participants);
  }

  private markOffline(key: string): void {
    const existing = this.m_participants.get(key);
    if (!existing || existing.isLocal) {
      debug("markOffline skipped", { key, found: Boolean(existing), isLocal: existing?.isLocal });
      return;
    }
    debug("markOffline", summarizeParticipant(existing));
    // Unity `OnServerCloseProject`: presenter flag + follow graph cleanup
    // before the leave lands (re-election follows via `ensurePresenter`).
    this.handleParticipantGone(key);
    const current = this.m_participants.get(key) ?? existing;
    this.m_participants.set(key, { ...current, isOnline: false, lastSeenAt: Date.now() });
    this.onParticipantsChanged.emit(this.participants);
  }

  private applyReservedColor(payload: Record<string, unknown>): void {
    const key = participantKeyFromPayload(payload);
    const colorIndex = numberField(payload, "color_index", "ColorIndex", "ci");
    if (!key || colorIndex === null) {
      debug("applyReservedColor skipped", {
        key,
        colorIndex,
        summary: summarizeRecord(payload),
      });
      return;
    }
    const existing = this.m_participants.get(key);
    if (!existing) {
      debug("applyReservedColor skipped: participant missing", {
        key,
        colorIndex,
        summary: summarizeRecord(payload),
      });
      return;
    }
    const color = PARTICIPANT_COLORS[colorIndex] ?? existing.color;
    const next = { ...existing, color, lastSeenAt: Date.now() };
    this.m_participants.set(key, next);
    debug("applyReservedColor", {
      colorIndex,
      participant: summarizeParticipant(next),
    });
    this.onParticipantsChanged.emit(this.participants);
  }

  private clear(): void {
    debug("clear participants");
    this.endLocalFollow(false);
    this.m_followerToFollowed.clear();
    this.m_participants.clear();
    this.m_localParticipantKey = null;
    this.m_lastPublishedTransform = null;
    this.m_lastTransformPublishAtMs = 0;
    this.m_localComposedTransform = null;
    this.m_lastPresenterChangeProcessedAt = 0;
    this.m_lastPublishedFollowKey = null;
    this.m_awaitingSharedStateUntil = 0;
    this.m_localActiveTool = 0;
    this.m_localHasHeadset = false;
    this.m_needsInitialSceneNavigation = false;
    this.m_initialSceneEmitPending = false;
    // Without this a sign-out/sign-in in the same tab leaves the last project
    // id cached, so re-opening that project early-returns in
    // `handleActiveProjectChanged` and never re-announces presence — the user
    // stays invisible to every peer for the rest of the session.
    this.m_lastProjectId = null;
    this.m_openProjectIdPendingClose = null;
    this.m_openProjectStartedAt = 0;
    this.m_loadedSceneIdPendingUnload = null;
    this.m_loadedSceneStartedAt = 0;
    this.onParticipantsChanged.emit([]);
  }
}

/**
 * Build a roster upsert from an inbound presence payload.
 *
 * Deliberately does NOT filter by project. The event relay is scoped to the
 * ACCOUNT, so events from users in other projects legitimately arrive here;
 * Unity records them all in `m_allParticipants` and scopes at READ time via
 * `IsInSession` (see `getSessionParticipants`). Filtering at ingest instead
 * was the bug: the guard could only fire when the payload carried a project
 * id, and the presence events that DON'T carry one — heartbeat above all —
 * sailed through and were admitted as "in no particular project", which every
 * downstream surface then treated as "in this one".
 *
 * `projectId` is left `undefined` when the payload has no project field at
 * all, so `upsert` keeps whatever it already knew. Only an explicit empty
 * string clears it.
 */
function participantFromPayload(payload: unknown): UpsertParticipant | null {
  const record = asRecord(payload);
  if (!record) {
    debug("participantFromPayload: non-object", payload);
    return null;
  }
  const projectId = projectIdField(record);
  const projectCustomerId = projectCustomerIdField(record);
  const userId =
    stringField(record, "UserId", "userId", "user_id", "OriginatingUserId", "originatingUserId", "uid") ?? "unknown";
  const appId =
    stringField(record, "AppInstanceId", "appInstanceId", "OriginatingAppInstanceId", "aid") ?? "app";
  const appName = stringField(record, "AppName", "appName", "app_name");
  const key = stringField(record, "LookupKey", "lookupKey", "participantKey", "key") ?? participantKey(userId, appId);
  const displayName =
    stringField(record, "DisplayName", "displayName", "Name", "name", "userTag", "initials") ?? userId;
  const rawInitials = stringField(record, "Initials", "initials");
  const initials = normalizeInitials(rawInitials, displayName, userId);
  const colorIndex = numberField(record, "color_index", "ColorIndex", "ci");
  const email = stringField(record, "Email", "email");
  const participant = {
    key,
    userId,
    appInstanceId: appId,
    appName,
    displayName,
    initials,
    email,
    projectId,
    projectCustomerId,
    color: colorIndex !== null
      ? PARTICIPANT_COLORS[colorIndex] ?? colorFromString(key)
      : stringField(record, "Color", "color") ?? colorFromString(key),
    isLocal: false,
    isOnline: true,
    // Tri-state, like the presence fields: only a roster payload carries the
    // device identity, so `null` here means "this event didn't say" and
    // `upsert` keeps what the peer already told us.
    kind: appName ? participantKindFor(appName) : null,
    hasHeadset: booleanField(record, "HasHeadset", "hasHeadset", "hd"),
    lastSeenAt: Date.now(),
    transform: transformFromPayload(record),
    // Tri-state: absent stays null so `upsert` keeps the known value.
    isPresenter: booleanField(record, "IsPresenter", "isPresenter", "is_presenter"),
    canBePresenter: booleanField(record, "CanBePresenter", "canBePresenter", "p"),
    currentSceneId: stringField(record, "CurrentSceneId", "currentSceneId", "sid"),
    activeTool: numberField(record, "at", "ActiveTool", "activeTool"),
    // Unity's int `ur`; the PascalCase spellings are the web's own legacy.
    userRole: numberField(record, "ur", "UserRole", "userRole"),
  };
  debug("participantFromPayload", {
    keys: Object.keys(record).sort(),
    summary: summarizeRecord(record),
    participant: summarizeParticipant(participant),
  });
  return participant;
}

/**
 * A shared-state row's last heartbeat, in unix epoch MILLISECONDS.
 *
 * Unity serializes `SessionParticipantData.LastHeartbeat` through the
 * `[SerializeField] private long timestamp` backing field
 * (`ProjectUserData.cs:113-121`, via `TimeUtils.ToUnixTimeMilliseconds`), so
 * `timestamp` is the wire name; the PascalCase spellings are defensive.
 */
function sharedStateHeartbeatMs(raw: unknown): number | null {
  const record = asRecord(raw);
  if (!record) return null;
  const ms = numberField(record, "timestamp", "LastHeartbeat", "lastHeartbeat");
  return ms !== null && ms > 0 ? ms : null;
}

/**
 * Is this shared-state row a live participant, or a GHOST the server has not
 * reaped yet? Returns `null` when the question cannot be answered — see the
 * fail-open note below.
 *
 * ⚠ THIS IS THE ONE THING THAT KEEPS A DEAD TAB FROM HOLDING PRESENTERSHIP.
 * Every other inbound path creates a participant we have just heard FROM, so
 * "alive" is free. A snapshot is different: it replays whatever the server
 * still holds, including a row for a client that closed without sending
 * `close_project`. Imported as live, such a row keeps its `is_presenter`, and
 * `ensurePresenter` — whose whole job is to elect when nobody is presenting —
 * sees a presenter and returns. With the full presenter gate that means no
 * tools, no gizmos and no scene switching, for the local 15 s eviction window,
 * restarted by every snapshot that re-imports the ghost.
 *
 * Unity computes the same verdict at `MultiuserAppModel.cs:1265`:
 *
 *     IsOffline = IdleTimeSeconds - LocalTimeDelta.TotalSeconds > k_maxSessionIdleSeconds
 *
 * which looks like it needs clock-skew tracking and does not. Expanding it —
 * `IdleTimeSeconds` is `nowLocal - lastHeartbeat` and `LocalTimeDelta` is
 * `nowLocal - serverNow` — the local clock cancels and it reduces to
 * `serverNow - lastHeartbeat > k_maxSessionIdleSeconds`. Both terms are the
 * SERVER's, both ride this very payload (`srvts` and the row's `timestamp`),
 * so the browser's clock never enters the comparison and there is nothing to
 * keep in sync. `LocalTimeDelta` exists upstream only to cancel a term Unity
 * had already introduced.
 *
 * FAILS OPEN, deliberately: a relay that omits `srvts` or the row stamp gets
 * `null` and the roster behaves exactly as it did before this check existed.
 * The alternative — treating "unknown" as idle — would empty the roster for
 * everyone on that relay, which is far worse than the bug being fixed.
 */
function sharedStateRowIsLive(raw: unknown, serverNowMs: number | null): boolean | null {
  if (serverNowMs === null || serverNowMs <= 0) return null;
  const heartbeatMs = sharedStateHeartbeatMs(raw);
  if (heartbeatMs === null) return null;
  return serverNowMs - heartbeatMs <= OFFLINE_AFTER_MS;
}

/**
 * Read a payload's project id as the TRI-STATE the roster merge needs:
 * `undefined` = the field is absent (keep what we know), `null` = present but
 * empty (Unity writes `ProjectId = ""` on `close_project`), else the id.
 *
 * `stringField` can't express this — it maps absent and empty alike to `null`.
 */
function projectIdField(record: Record<string, unknown>): string | null | undefined {
  return presentStringField(record, "ProjectId", "projectId", "project_id");
}

/**
 * Unity `SessionParticipantData.ProjectCustomerId`. Shared-state rows carry it
 * as `act_id`; `open_project` carries it as `CustomerId`, which
 * `OnServerOpenProject` assigns to the same field (`MultiuserAppModel.cs:1427`)
 * — so both spellings must be read, or a Unity peer's `open_project` leaves
 * the account term unset.
 */
function projectCustomerIdField(record: Record<string, unknown>): string | null | undefined {
  return presentStringField(
    record,
    "act_id",
    "ProjectCustomerId",
    "projectCustomerId",
    "CustomerId",
    "customerId",
  );
}

function presentStringField(
  record: Record<string, unknown>,
  ...keys: string[]
): string | null | undefined {
  let sawKey = false;
  for (const key of keys) {
    const value = record[key];
    if (value === undefined) continue;
    sawKey = true;
    if (typeof value === "string" && value.length > 0) return value;
  }
  return sawKey ? null : undefined;
}

function participantKeyFromPayload(payload: unknown): string | null {
  const record = asRecord(payload);
  if (!record) return null;
  const explicit = stringField(record, "LookupKey", "lookupKey", "participantKey", "key");
  if (explicit) return explicit;
  // `user_id` matters: Unity `SessionParticipantData` rows (shared state)
  // serialize the user id there — their `uid` is the unrelated (usually
  // empty) FirebaseDocumentId. Keep the chain aligned with
  // `participantFromPayload` so both derive the same key for a row.
  const userId = stringField(
    record,
    "UserId",
    "userId",
    "user_id",
    "OriginatingUserId",
    "originatingUserId",
    "uid",
  );
  const appId = stringField(record, "AppInstanceId", "appInstanceId", "OriginatingAppInstanceId", "aid");
  return userId && appId ? participantKey(userId, appId) : null;
}

function transformFromPayload(payload: unknown): ParticipantTransform | null {
  const record = asRecord(payload);
  const transform = asRecord(record?.Transform) ?? asRecord(record?.transform) ?? asRecord(record?.t) ?? record;
  if (!transform) return null;
  const position = vec3From(transform.Position ?? transform.position ?? transform.p);
  const rotation = quatFrom(transform.Rotation ?? transform.rotation ?? transform.r);
  if (!position) return null;
  const toolPosition = vec3From(transform.ToolPosition ?? transform.toolPosition ?? transform.tp);
  const toolVisibility = numberField(transform, "ToolVisibility", "toolVisibility", "tv") ?? 0;
  const toolPanelVisibility =
    booleanField(transform, "ToolPanelVisibility", "toolPanelVisibility", "tpv") ?? false;
  const controllerVisibilityState =
    numberField(
      transform,
      "m_controllerVisibilityState",
      "ControllerVisibilityState",
      "controllerVisibilityState",
    ) ?? 0;
  return {
    position,
    rotation: rotation ?? [0, 0, 0, 1],
    toolPosition,
    toolVisibility,
    toolPanelVisibility,
    controllerVisibilityState,
  };
}

function vec3From(value: unknown): [number, number, number] | null {
  if (Array.isArray(value) && value.length >= 3) {
    const nums = value.slice(0, 3).map(Number);
    return nums.every(Number.isFinite) ? [nums[0], nums[1], nums[2]] : null;
  }
  const record = asRecord(value);
  if (!record) return null;
  const nums = [Number(record.x ?? record.X), Number(record.y ?? record.Y), Number(record.z ?? record.Z)];
  return nums.every(Number.isFinite) ? [nums[0], nums[1], nums[2]] : null;
}

function quatFrom(value: unknown): [number, number, number, number] | null {
  if (Array.isArray(value) && value.length >= 4) {
    const nums = value.slice(0, 4).map(Number);
    return nums.every(Number.isFinite) ? [nums[0], nums[1], nums[2], nums[3]] : null;
  }
  const record = asRecord(value);
  if (!record) return null;
  const nums = [
    Number(record.x ?? record.X),
    Number(record.y ?? record.Y),
    Number(record.z ?? record.Z),
    Number(record.w ?? record.W),
  ];
  return nums.every(Number.isFinite) ? [nums[0], nums[1], nums[2], nums[3]] : null;
}

function displayNameFor(user: UserInfo): string {
  return user.displayName || user.email || user.userId || "You";
}

function nameTagFor(user: UserInfo): string {
  // `UserInfo.nameTag` now carries Unity's `UserInfo.NameTag` derivation
  // (see `models/auth/types.ts`); the fallbacks below only cover a UserInfo
  // built before that field existed.
  return user.nameTag || displayNameFor(user);
}

function initialsForUser(user: UserInfo): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  return initialsFor(displayNameFor(user));
}

function initialsFor(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || "U").toUpperCase();
}

function transformToWire(transform: ParticipantTransform): Record<string, unknown> {
  return {
    p: vec3ToWire(transform.position),
    r: quatToWire(transform.rotation),
    tp: vec3ToWire(transform.toolPosition ?? [0, 0, 0]),
    tv: transform.toolVisibility ?? 0,
    tpv: transform.toolPanelVisibility ?? false,
    m_controllerVisibilityState: transform.controllerVisibilityState ?? 0,
  };
}

/**
 * The transform stamped on heartbeat / open_project / launch_application
 * payloads. Unity substitutes `k_notIntializedPos` (10000,10000,10000) when
 * the user has no real pose yet — never the (0,0,0) sentinel (AI-1188).
 */
function publishableTransform(transform: ParticipantTransform | null): ParticipantTransform {
  if (transform && !isZeroPosition(transform.position)) return transform;
  return {
    position: K_NOT_INITIALIZED_POS,
    rotation: [0, 0, 0, 1],
  };
}

function isZeroPosition(position: ParticipantVec3): boolean {
  return position[0] === 0 && position[1] === 0 && position[2] === 0;
}

/**
 * Unity quantizes `ParticipantTransformData` on assignment: positions round
 * to millimetres, quaternion components to 2 decimals, and the invalid
 * all-zero quaternion coerces to identity (`SharedStateEventData.cs`).
 */
function quantizeTransform(transform: ParticipantTransform): ParticipantTransform {
  const r = transform.rotation;
  const isZeroQuat = r[0] === 0 && r[1] === 0 && r[2] === 0 && r[3] === 0;
  return {
    position: roundVec3Mm(transform.position),
    rotation: isZeroQuat
      ? [0, 0, 0, 1]
      : [round2(r[0]), round2(r[1]), round2(r[2]), round2(r[3])],
    toolPosition: transform.toolPosition ? roundVec3Mm(transform.toolPosition) : transform.toolPosition,
    toolVisibility: transform.toolVisibility ?? 0,
    toolPanelVisibility: transform.toolPanelVisibility ?? false,
    controllerVisibilityState: transform.controllerVisibilityState ?? 0,
  };
}

/**
 * Unity `ParticipantTransformData.Equals`: position + tool position within
 * 1 cm (squared magnitude), rotation within 5°, and exact equality on the
 * tool-visibility / tool-panel / controller-visibility fields.
 */
function transformsApproximatelyEqual(a: ParticipantTransform, b: ParticipantTransform): boolean {
  return (
    sqDistance(a.position, b.position) <= K_MIN_REQUIRED_POS_MAGNITUDE_SQ
    && quatAngleDeg(a.rotation, b.rotation) < K_MIN_REQUIRED_ROT_ANGLE_DEG
    && sqDistance(a.toolPosition ?? [0, 0, 0], b.toolPosition ?? [0, 0, 0])
      <= K_MIN_REQUIRED_POS_MAGNITUDE_SQ
    && (a.toolVisibility ?? 0) === (b.toolVisibility ?? 0)
    && (a.toolPanelVisibility ?? false) === (b.toolPanelVisibility ?? false)
    && (a.controllerVisibilityState ?? 0) === (b.controllerVisibilityState ?? 0)
  );
}

function sqDistance(a: ParticipantVec3, b: ParticipantVec3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}

function quatAngleDeg(a: ParticipantQuat, b: ParticipantQuat): number {
  const magA = Math.hypot(a[0], a[1], a[2], a[3]);
  const magB = Math.hypot(b[0], b[1], b[2], b[3]);
  if (magA === 0 || magB === 0) return 0;
  const dot = (a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]) / (magA * magB);
  const clamped = Math.min(1, Math.abs(dot));
  return (2 * Math.acos(clamped) * 180) / Math.PI;
}

function roundVec3Mm(value: ParticipantVec3): ParticipantVec3 {
  return [roundMm(value[0]), roundMm(value[1]), roundMm(value[2])];
}

function roundMm(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function vec3ToWire(value: readonly [number, number, number]): Record<string, number> {
  return { x: value[0], y: value[1], z: value[2] };
}

function quatToWire(value: readonly [number, number, number, number]): Record<string, number> {
  return { x: value[0], y: value[1], z: value[2], w: value[3] };
}

function participantKey(userId: string, appInstanceId: string): string {
  return `${userId}:${appInstanceId}`;
}

function matches(type: string, ...names: string[]): boolean {
  return names.some((name) => type === name.toLowerCase());
}

function arrayField(value: Record<string, unknown>, ...keys: ReadonlyArray<string>): unknown[] {
  for (const key of keys) {
    const field = value[key];
    if (Array.isArray(field)) return field;
  }
  return [];
}

function stringField(value: Record<string, unknown>, ...keys: ReadonlyArray<string>): string | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "string" && field.length > 0) return field;
  }
  return null;
}

/**
 * Like `stringField` but an EMPTY string is a meaningful value (returned as
 * ""), matching Unity's null-coalesce-to-"" semantics for fields like the
 * shared-state `fu`, where "" means "not following" and must clear state.
 */
function rawStringField(value: Record<string, unknown>, ...keys: ReadonlyArray<string>): string | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "string") return field;
  }
  return null;
}

function numberField(value: Record<string, unknown>, ...keys: ReadonlyArray<string>): number | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "number" && Number.isFinite(field)) return field;
    if (typeof field === "string" && field.trim().length > 0) {
      const parsed = Number(field);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function booleanField(value: Record<string, unknown>, ...keys: ReadonlyArray<string>): boolean | null {
  for (const key of keys) {
    const field = value[key];
    if (typeof field === "boolean") return field;
    if (typeof field === "number" && Number.isFinite(field)) return field !== 0;
    if (typeof field === "string" && field.trim().length > 0) {
      const normalized = field.trim().toLowerCase();
      if (normalized === "true" || normalized === "1") return true;
      if (normalized === "false" || normalized === "0") return false;
    }
  }
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function participantKindFor(appName: string | null): MultiuserParticipantKind {
  if (!appName) return "unknown";
  if (appName === DESKTOP_PRODUCT_ID_DEV || appName === DESKTOP_PRODUCT_ID_PROD) return "desktop";
  if (appName === REMOTE_PRODUCT_ID_DEV || appName === REMOTE_PRODUCT_ID_PROD) return "remote";
  if (appName === TABLET_PRODUCT_ID_DEV || appName === TABLET_PRODUCT_ID_PROD) return "tablet";
  if (ANDROID_PRODUCT_IDS.has(appName) || QUESTLINK_PRODUCT_IDS.has(appName)) return "quest";
  return "unknown";
}

function colorIndexFromString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  return Math.abs(hash) % PARTICIPANT_COLORS.length;
}

function chooseDisplayName(
  existing: MultiuserParticipant | undefined,
  incoming: UpsertParticipant,
): string {
  if (!existing) return incoming.displayName;
  if (incoming.displayName && incoming.displayName !== incoming.userId && incoming.displayName !== incoming.initials) {
    return incoming.displayName;
  }
  return existing.displayName || incoming.displayName;
}

function chooseInitials(
  existing: MultiuserParticipant | undefined,
  incoming: UpsertParticipant,
): string {
  const generatedFromUserId = initialsFor(incoming.userId);
  const generatedFromName = initialsFor(incoming.displayName);
  const incomingLooksGenerated = incoming.initials === generatedFromUserId || isNumericInitials(incoming.initials);
  if (!existing) {
    return incomingLooksGenerated && generatedFromName !== generatedFromUserId
      ? generatedFromName
      : incoming.initials;
  }
  if (incoming.initials && !incomingLooksGenerated) {
    return incoming.initials;
  }
  return existing.initials || incoming.initials;
}

function normalizeInitials(rawInitials: string | null, displayName: string, userId: string): string {
  const generatedFromUserId = initialsFor(userId);
  const generatedFromName = initialsFor(displayName);
  if (!rawInitials || rawInitials === generatedFromUserId || isNumericInitials(rawInitials)) {
    return generatedFromName;
  }
  return rawInitials;
}

function isNumericInitials(value: string): boolean {
  return /^\d{2}$/.test(value);
}

function chooseColor(
  existing: MultiuserParticipant | undefined,
  incoming: UpsertParticipant,
): string {
  if (!existing) return incoming.color;
  const generated = colorFromString(incoming.key);
  if (incoming.color && incoming.color !== generated) return incoming.color;
  return existing.color || incoming.color;
}

function colorFromString(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return hslToHex(hue, 72, 54);
}

function hslToHex(h: number, s: number, l: number): string {
  const saturation = s / 100;
  const lightness = l / 100;
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = lightness - c / 2;
  const [r, g, b] = h < 60
    ? [c, x, 0]
    : h < 120
      ? [x, c, 0]
      : h < 180
        ? [0, c, x]
        : h < 240
          ? [0, x, c]
          : h < 300
            ? [x, 0, c]
            : [c, 0, x];
  return `#${toHex(r + m)}${toHex(g + m)}${toHex(b + m)}`;
}

function toHex(value: number): string {
  return Math.round(value * 255).toString(16).padStart(2, "0");
}

function summarizeParticipant(participant: UpsertParticipant): Record<string, unknown> {
  return {
    key: participant.key,
    userId: participant.userId,
    appInstanceId: participant.appInstanceId,
    displayName: participant.displayName,
    initials: participant.initials,
    projectId: participant.projectId,
    isLocal: participant.isLocal,
    isOnline: participant.isOnline,
  };
}

function summarizeRecord(record: Record<string, unknown>): Record<string, unknown> {
  return {
    uid: record.uid ?? record.OriginatingUserId ?? record.user_id ?? record.UserId,
    aid: record.aid ?? record.OriginatingAppInstanceId ?? record.AppInstanceId,
    projectId: record.projectId ?? record.project_id ?? record.ProjectId,
    customerId: record.customerId ?? record.CustomerId ?? record.act_id,
    name: record.name ?? record.userTag ?? record.DisplayName,
    initials: record.initials ?? record.Initials,
  };
}

function debug(...args: ReadonlyArray<unknown>): void {
  if (!DEBUG_MULTIUSER) return;
  console.debug("[MultiuserSessionModel]", ...args);
}
